import type { Clave } from "./es";

/**
 * El programa en inglés, para los socios y los clubes que no hablan castellano.
 *
 * `Record<Clave, string>` es lo que hace que no se pueda olvidar ninguna
 * frase: si es.ts gana una clave y aquí no está, el programa no compila. Es
 * mejor que una prueba, porque salta antes de ejecutar nada.
 */
export const en: Record<Clave, string> = {
  "general.cargando": "Loading...",
  "general.unMomento": "One moment...",
  "general.guardando": "Saving...",
  "general.guardar": "Save",
  "general.guardado": "Saved",
  "general.cancelar": "Cancel",
  "general.cerrar": "Close",
  "general.volver": "Back",
  "general.salir": "Sign out",
  "general.entrar": "Sign in",
  "general.enviando": "Sending...",
  "general.idioma": "Language",
  "general.o": "or",
  "general.fallo": "Something went wrong",

  // El titulo de la pestaña: a menudo hay varias abiertas.
  "titulo.gestion": "Club management",
  "titulo.portal": "My club",
  "titulo.carta": "Price board",
  "titulo.plataforma": "Platform dashboard",
  "titulo.web": "asocis · Software for cannabis associations",
  "titulo.prueba": "Ask for a trial · asocis",
  "manifiesto.nombre": "Member portal",
  "manifiesto.descripcion":
    "Your card, your consumption, your fee and your association's news.",
  "carta.cargando": "Loading the board…",
  "carta.noDisponible": "Board not available",
  "carta.noDisponibleAyuda":
    "Check the link, or ask the club to switch the board on in its settings.",
  "carta.sinProducto": "Nothing available right now.",
  "carta.pie": "Solidarity contribution for members · Updated at {hora}",
  "carta.pieSinConexion":
    "Solidarity contribution for members · Updated at {hora} · offline, this may be out of date",
  "unidad.dia": "day",
  "unidad.dias": "days",

  "mov.recarga.EFECTIVO": "Cash top-up",
  "mov.recarga.TARJETA": "Card top-up",
  "mov.recarga.TRANSFERENCIA": "Transfer top-up",
  "mov.devolucion": "Balance refund",
  "mov.dispensacion": "Dispensing #{numero}",
  "mov.dispensacionAnulada": "Dispensing #{numero} cancelled",
  "mov.barra": "Bar #{numero}",
  "mov.barraAnulada": "Bar #{numero} cancelled",
  "mov.donacion": "Donation to the club",
  "cuota.anual": "Annual fee",
  "cuota.anios": "{n}-year fee",
  "cuota.unMes": "One-month fee",
  "cuota.meses": "{n}-month fee",
  "cuota.alta": "Joining fee",
  "pago.EFECTIVO": "Cash",
  "pago.CARTERA": "Wallet",
  "pago.TARJETA": "Card",
  "pago.TRANSFERENCIA": "Transfer",
  "movimiento.RECARGA": "Top-up",
  "movimiento.CONSUMO": "Payment",
  "movimiento.DEVOLUCION": "Refund",
  "movimiento.AJUSTE": "Adjustment",
  "movimiento.CUOTA": "Membership fee",
  "genetica.SATIVA": "Sativa",
  "genetica.INDICA": "Indica",
  "genetica.HIBRIDA": "Hybrid",
  "genetica.CBD": "CBD",
  "genetica.NA": "—",

  "portal.cabecera.socioNumero": "{nombre} · member no. {numero}",
  "portal.cabecera.contrasena": "Password",
  "portal.pestana.inicio": "My card",
  "portal.pestana.catalogo": "In stock",
  "portal.pestana.reservas": "My orders",
  "portal.pestana.noticias": "News",
  "portal.pestana.valoraciones": "Reviews",
  "portal.pestana.historial": "My history",
  "portal.pestana.contrato": "Agreement",

  "entrada.titulo": "Member portal",
  "entrada.subtituloEntrar": "Check your consumption, your fee and your card",
  "entrada.subtituloActivar": "Set up your account with the club's code",
  "entrada.club": "Club",
  "entrada.clubAyuda": "The club name the association gave you.",
  "entrada.numeroSocio": "Member number",
  "entrada.codigo": "Activation code",
  "entrada.codigoAyuda": "You get it at the counter. It expires after 7 days.",
  "entrada.contrasena": "Password",
  "entrada.eligeContrasena": "Choose a password",
  "entrada.minimo8": "At least 8 characters.",
  "entrada.repiteContrasena": "Repeat the password",
  "entrada.crearAcceso": "Create my account",
  "entrada.primeraVez": "First time?",
  "entrada.activarAcceso": "Activate my account",
  "entrada.yaTengoContrasena": "I already have a password",
  "entrada.noCoinciden": "The two passwords don't match",
  "entrada.accesoCreado": "Account created. You can sign in with your password now.",

  "inicio.cuotaVencioHace": "Your membership expired {dias} ago. Pop into the club to renew it.",
  "inicio.cuotaVenceHoy": "Your membership expires today.",
  "inicio.cuotaVenceEn": "Your membership expires in {dias}.",
  "inicio.socio": "Member",
  "inicio.numero": "No. {numero}",
  "inicio.socioDesde": "Member since {fecha}",
  "inicio.cuotaValidaHasta": "Member until {fecha}",
  "inicio.generando": "Generating…",
  "inicio.ensenaCodigo": "Show this code at the counter to identify yourself.",
  "inicio.tuConsumo": "Your consumption",
  "inicio.hoy": "Today",
  "inicio.esteMes": "This month",
  "inicio.puedesRetirar": "You can collect now",
  "inicio.tuCartera": "Your wallet",
  "inicio.saldoDisponible": "Balance available for your contributions. Top it up at the counter.",
  "inicio.tuClub": "Your club",
  "inicio.telefono": "Phone:",

  "avisos.titulo": "Club alerts",
  "avisos.noSoportado": "This browser doesn't support alerts. You can still read the news by signing in to the portal.",
  "avisos.requiereInstalar1": "On iPhone you have to add the portal to your home screen first: tap",
  "avisos.compartir": "Share",
  "avisos.yLuego": "and then",
  "avisos.anadirAInicio": "Add to Home Screen",
  "avisos.requiereInstalar2": "After that you can turn alerts on from here.",
  "avisos.activados": "You get an alert on this device when the club posts news.",
  "avisos.sinActivar": "Turn alerts on to hear the club's news without signing in.",
  "avisos.desactivar": "Turn alerts off",
  "avisos.activar": "Turn alerts on",
  "avisos.enviarPrueba": "Send a test",
  "avisos.yaNo": "You won't get alerts on this device any more.",
  "avisos.listo": "Done. We'll alert you when the club posts something.",
  "avisos.bloqueados": "Your browser is blocking notifications for this page. You can unblock them in its settings.",
  "avisos.fallo": "Couldn't turn the alerts on. Try again later.",
  "avisos.pruebaEnviada": "Test alert sent: it should reach you in a few seconds.",
  "avisos.pruebaFallo": "Couldn't send the test alert.",

  "catalogo.vacio": "The club hasn't got any products up right now.",
  "catalogo.condiciones": "These contributions already include your member terms. What's available can change during the day.",
  "catalogo.sinSaldo":
    "To order you need a balance in your wallet. Pop into the club to top it up and you can order from here.",
  "catalogo.sinStock": "Out of stock",
  "catalogo.quitarGramo": "Remove a gram",
  "catalogo.anadirGramo": "Add a gram",
  "catalogo.faltan": "Your wallet's {importe} short",
  "catalogo.quedarian": "You'd have {importe} left in your wallet",
  "catalogo.vaciar": "Clear",
  "catalogo.reservar": "Order",
  "catalogo.reservando": "Ordering...",
  "catalogo.falloReserva": "Couldn't place the order",
  "reservas.vacio":
    "You haven't ordered anything yet. From ‘Available’ you can order what you want and come and collect it.",
  "reservas.titulo": "Order {numero}",
  "reservas.PENDIENTE": "Pending",
  "reservas.PENDIENTE.explica": "The club hasn't got it ready yet.",
  "reservas.PREPARADA": "Ready",
  "reservas.PREPARADA.explica": "It's ready for you. Come and collect it.",
  "reservas.ENTREGADA": "Collected",
  "reservas.ENTREGADA.explica": "Collected.",
  "reservas.CANCELADA": "Cancelled",
  "reservas.CADUCADA": "Expired",
  "reservas.CADUCADA.explica": "The deadline passed without you collecting it.",
  "reservas.total": "Total {cantidad}",
  "reservas.guardadaHasta": "Held until {fecha}. After that day it expires.",
  "reservas.falloCancelar": "Couldn't cancel",

  "noticias.vacio": "The club hasn't posted anything yet.",

  "valoraciones.vacio":
    "Once you've collected something, you can say here what you thought of it. It helps the club know what to order again.",
  "valoraciones.intro":
    "Say what you thought of what you collected. The club reads it, so it knows what to order again.",
  "valoraciones.nDe5": "{n} out of 5",
  "valoraciones.comentario": "Anything you want to tell us (optional)",

  "historial.retiradas": "Your collections",
  "historial.sinRetiradas": "You haven't collected anything yet.",
  "historial.fecha": "Date",
  "historial.productos": "Products",
  "historial.cantidad": "Quantity",
  "historial.aportacion": "Contribution",
  "historial.anulada": "Void",
  "historial.cartera": "Your wallet activity",
  "historial.sinMovimientos": "No activity.",
  "historial.concepto": "Description",
  "historial.importe": "Amount",
  "historial.saldo": "Balance",
  "historial.cuotas": "Your membership fees",
  "historial.sinCuotas": "No fees recorded.",
  "historial.validaHasta": "Valid until",

  "contrato.sinPublicar": "Your association hasn't put its membership agreement up here yet.",
  "contrato.aceptaste": "You accepted",
  "contrato.version": "version {numero}",
  "contrato.elDia": "on {fecha}",
  "contrato.esLaQueAplica": ". That's the one that applies to you.",
  "contrato.leeConCalma":
    "Take your time with it. When you accept, the date and the version are recorded, and you can come back and read it here whenever you like.",
  "contrato.versionNueva":
    "The association has put up a newer version. What you signed is still the one above; ask at the club if you want to accept the new one.",
  "contrato.aceptar": "I've read and accept the agreement",
  "contrato.falloGuardar": "Couldn't save",

  "contrasena.titulo": "Change my password",
  "contrasena.actual": "Current password",
  "contrasena.nueva": "New password",
  "contrasena.repite": "Repeat the new one",
  "contrasena.cambiar": "Change",
  "contrasena.cambiada": "Password changed.",
  "contrasena.fallo": "Couldn't change the password",

  "alta.titulo": "Apply to join",
  "alta.clubNoEncontrado": "We can't find that club. Check the link you were given.",
  "alta.nombre": "First name *",
  "alta.apellidos": "Surname *",
  "alta.documento": "ID",
  "alta.pasaporte": "Passport",
  "alta.numero": "Number *",
  "alta.fechaNacimiento": "Date of birth *",
  "alta.mayorDeEdad": "You have to be over 18.",
  "alta.telefono": "Phone",
  "alta.email": "Email",
  "alta.ciudad": "Town",
  "alta.avalista": "Sponsor's member number",
  "alta.avalistaAyuda": "If someone in the association is putting you forward.",
  "alta.mensaje": "Message for the club",
  "alta.consentimiento": "I agree to the association processing my data to consider this application.",
  "alta.consentimientoAyuda": "If they turn it down, your data stays in their internal record only.",
  "alta.enviar": "Send application",
  "alta.yaSoySocio": "I'm already a member",
  "alta.enviada": "Application sent",
  "alta.enviadaTexto":
    "{club} will look at it and get in touch with you. Remember you finish signing up at the club itself, with your ID.",
  "alta.elClub": "The club",
  "alta.fallo": "Couldn't send the application",

  "err.clubNoEncontrado": "We can't find that club",
  "err.codigoNoValido": "Wrong member number or code, or the code has expired",
  "err.entradaIncorrecta": "Wrong member number or password",
  "err.passwordActualMal": "Your current password isn't right",
  "err.soloLoQueTeLlevaste": "You can only review what you've collected",
  "err.sinContrato": "This club hasn't put its membership agreement up yet",
  "err.reservaNoEncontrada": "We can't find that order",
  "err.reservaNoPendiente": "This order isn't pending any more",
  "err.reservaCerrada": "This order is already closed",
  "err.yaTienesReserva":
    "You've already got order {numero} to collect. Cancel it if you want to order something else.",
  "err.productoNoEncontrado": "We can't find that product",
  "err.pasaDelMes": "That's more than you can collect this month. You've got {cantidad} left.",
  "err.sinSaldo":
    "This comes to {total} and your wallet's got {saldo}. Pop into the club to top it up and try again.",
  "err.mayorDeEdad": "You have to be over 18",
  "err.nacimientoNoValido": "That date of birth isn't valid",
  "err.consentimiento": "You have to agree to the association processing your data",
  "err.idiomaNoExiste": "That language isn't available",
  "err.bloqueo.baja": "Your membership has been cancelled. Have a word with the club.",
  "err.bloqueo.suspendido": "Your membership is suspended. Have a word with the club.",
  "err.bloqueo.pendiente":
    "Your application still has to be approved. Pop into the club to finish it.",
  "err.bloqueo.cuotaCaducada": "Your membership has expired. Pop into the club to renew it.",
  "err.bloqueo.cuotaVencida": "Your membership needs renewing. Pop into the club.",
  "servidor.El socio está dado de baja": "This member has left",
  "servidor.El socio está suspendido": "This member is suspended",
  "servidor.El alta del socio está pendiente de validar": "This member's application still has to be approved",
  "servidor.La cuota del socio está caducada": "This member's membership has expired",
  "servidor.La cuota del socio ha caducado: hay que renovarla":
    "This member's membership has expired: it has to be renewed",

  "web.entrar": "Sign in",
  "web.frase":
    "asocis is the software for running a cannabis association: members, counter, batch inventory, till and member portal.",
  "web.fraseSegunda":
    "Below is its counter, running on made-up data. Pick a member, tap products, go over their limit, take the money.",
  "web.altasUnaAUna":
    "We sign clubs up one at a time: we give you a code and with it you set your club up in a minute.",
  "web.yaTienesUno": "Got one already?",
  "web.pedirPrueba": "Ask for a trial",
  "web.pedirAcceso": "Ask for access",
  "web.probarGratis": "Try {dias} free",
  "web.crearClub": "Create my club",
  "web.pieDemo":
    "With your data it does the same. What doesn't fit here: the search by name, number or ID; the QR membership card reader; the catalogue categories; manual discounts; and the rest of the navigation.",

  "web.detras": "What's behind the counter",
  "web.detras.socios": "Members",
  "web.detras.socios.texto":
    "Joining with ID, photo, sponsor, declared consumption and dated consent. Membership types with their fee, their discount and their limits. The member register the association has to keep, ready to print. And ‘Import CSV’ to bring them over from your old software.",
  "web.detras.limites": "Limits",
  "web.detras.limites.texto":
    "Every member has a daily cap and a monthly one. The counter compares the order with what they've had; if it goes over, it has to be authorised and who did it is recorded. A member with an expired fee gets nothing dispensed until they renew.",
  "web.detras.inventario": "Batch inventory",
  "web.detras.inventario.texto":
    "Stock comes in by batch, with supplier, cost and expiry. The counter takes from the oldest, waste carries a reason and stock counts compare what you counted with what there should be. For every gram dispensed you know which batch it came from.",
  "web.detras.caja": "Till",
  "web.detras.caja.texto":
    "The shift opens with a float, then takes in dispensing and bar, and cash expenses go out of it, and at close you count the drawer: the software tells you what should be there and the difference. Direct debit expenses don't touch the till.",
  "web.detras.cartera": "Wallet",
  "web.detras.cartera.texto":
    "The member's prepaid wallet: top-ups, payments, refunds. At the counter you take it from the wallet with one tap, and if it doesn't cover it, it says so.",
  "web.detras.barra": "Bar",
  "web.detras.barra.texto":
    "Coffees, soft drinks and snacks with their own menu and quick sale. They go to the same till, but the cash count keeps bar apart from dispensing.",
  "web.detras.portal": "Member portal",
  "web.detras.portal.texto":
    "On their phone, like an app: the membership card with its QR code to identify themselves at the counter, their consumption against their limits, their wallet, the catalogue with its prices, the club's news and orders to come and collect. In {idiomas}.",
  "web.detras.cuotas": "Fees and renewals",
  "web.detras.cuotas.texto":
    "Taking the money, expiry dates, and an email reminder before they expire. Donations go separately, so they don't move anyone's date.",
  "web.detras.gastos": "Expenses and purchases",
  "web.detras.gastos.texto":
    "What you owe the supplier and what you've paid, with category, on the same screen. Export for your accountant.",
  "web.detras.acceso": "Check-in and capacity",
  "web.detras.acceso.texto":
    "Who's inside now and how many people have come through today. Set a capacity and it alerts you when it fills up: it doesn't shut the door.",
  "web.detras.informes": "Reports",
  "web.detras.informes.texto":
    "Where the money comes from by day or by month, keeping dispensing, bar and fees apart. Rankings of products and of members.",
  "web.detras.carta": "Menu for the screen in the club",
  "web.detras.carta.texto":
    "A full-screen page with what's in today and its price, that survives the network going down.",

  "web.precio": "Price",
  "web.precio.texto":
    "It goes by size, and it's the <b>active</b> members that count: members who left in earlier years don't push your price up. Every feature and your whole team are in every band.",
  "web.precio.sociosActivos": "Active members",
  "web.precio.alMes": "A month",
  "web.precio.hablamos": "Let's talk",
  "web.precio.consultanos": "Ask us",
  "web.tramo.hasta": "Up to {hasta} members",
  "web.tramo.entre": "{desde} to {hasta} members",
  "web.tramo.mas": "More than {desde} members",
  "web.precio.prueba": "{dias} trial, to see it with your own real data.",
  "web.precio.condiciones":
    "No lock-in: if you leave, you take your data with you, and you can download the whole club in one file whenever you like, the day you go as well. If the club grows and moves band, we tell you first; nothing ever gets locked. Updates are included.",

  "web.datos": "About the data",
  "web.datos.texto":
    "A club handles ID documents, photos and its members' consumption: personal data about real people. Each club is walled off from the rest, passwords are stored encrypted and everything anyone does is kept in an activity log with who and when. Every club is backed up each night, and the club can download the backup itself. The tablet at the counter locks itself if nobody touches it.",
  "web.datos.nota":
    "The software gives you the tools; the data processing agreement with your members and the retention policy are yours to set, as with any other supplier.",
  "web.empezar": "How you start",
  "web.empezar.nosEscribes": "You write to us",
  "web.empezar.conCodigo":
    "and we reply with a sign-up code. One at a time on purpose: every club starts with someone on the other end.",
  "web.empezar.creasClub": "You create your club",
  "web.empezar.sinCodigo": "with the name, the association's details and your owner account. One minute.",
  "web.empezar.despues":
    "Then you set your membership types, the fee, the limits and the catalogue —whatever you leave out comes with a sensible value— and you bring your members over with ‘Import CSV’, which reads the register from your old software. Same day, you've got a counter.",
  "web.pie": "asocis · Cannabis association management",
  "web.pie.avisoLegal": "Legal notice",
  "web.pie.privacidad": "Privacy",

  "web.demoEnCastellano":
    "This sample counter is in Spanish only; the real software and the member portal are in {idiomas}.",
  "prueba.titulo": "Ask for a trial",
  "prueba.intro":
    "We sign clubs up one at a time: we reply with a code and with it you set your club up in a minute. Tell us how many members you have and we'll tell you straight away what you'd pay.",
  "prueba.nombreClub": "Association name",
  "prueba.ciudad": "Town",
  "prueba.socios": "Members, roughly",
  "prueba.sociosAyuda": "Optional. It's what sets the price.",
  "prueba.tuNombre": "Your name",
  "prueba.tuEmail": "Your email",
  "prueba.tuEmailAyuda": "That's where we send the code.",
  "prueba.telefono": "Phone",
  "prueba.telefonoAyuda": "Optional, in case it's easier to call you.",
  "prueba.mensaje": "Anything we should know?",
  "prueba.mensajeAyuda": "Optional. How you run things now, what you're missing, when you want to start.",
  "prueba.privacidad1": "I've read the",
  "prueba.privacidad2": "privacy policy",
  "prueba.privacidad3": "and I agree to you using this data to get back to me.",
  "prueba.enviar": "Ask for the trial",
  "prueba.fallo": "Couldn't send the request",
  "prueba.recibido": "Got it.",
  "prueba.recibidoTexto1": "We'll email you at",
  "prueba.recibidoTexto2": "with the code to set up",
  "prueba.recibidoTexto3": ", usually within one working day. If you don't see it, check your junk mail.",
  "prueba.volverWeb": "Back to the website",

  "g.cargando": "Loading...",
  "g.cancelar": "Cancel",
  "g.cerrar": "Close",
  "g.guardar": "Save",
  "g.anterior": "Previous",
  "g.siguiente": "Next",
  "g.pagina": "Page {pagina} of {paginas}",
  "g.estado.ACTIVO": "Active",
  "g.estado.PENDIENTE": "Pending",
  "g.estado.CADUCADO": "Expired",
  "g.estado.SUSPENDIDO": "Suspended",
  "g.estado.BAJA": "Left",

  "m.titulo": "Counter",
  "m.descripcion": "Record a collection for a member",
  "m.dudaDeGuardado1": "Check",
  "m.dudaDeGuardado2": "to see if the collection was recorded. If it's there, don't do it again.",
  "m.dispensaciones": "Dispensing",
  "m.reservas": "Orders to collect",
  "m.socio": "Member",
  "m.pedido": "Items",
  "m.total": "Total",
  "m.reservaNumero": "Order {numero}",
  "m.faltaGenero": "Not enough stock",
  "m.cancelarReserva": "Cancel",
  "m.can.titulo": "Cancel order {numero}",
  "m.can.quePasa":
    "The member will see the order cancelled, with the reason you write, and can order again. If they've got the portal on their phone, they get an alert.",
  "m.can.motivo": "Reason (the member reads it)",
  "m.can.confirmar": "Yes, cancel the order",
  "m.can.fallo": "Couldn't cancel the order",
  "m.can.hecho": "Order cancelled",
  "m.atender": "Serve",
  "m.aQuien": "Who are you dispensing to?",
  "m.comoBuscar": "Type at least two letters of the name, the member number or the ID.",
  "m.todo": "All",
  "m.sinProductos": "No products available in this category.",
  "m.reservado": "{cantidad} on order",
  "m.sinStock": "Out of stock",
  "m.cambiar": "Change",
  "m.hoy": "Today",
  "m.esteMes": "This month",
  "m.cartera": "Wallet",
  "m.ingresar": "+ Top up",
  "m.ingresarTitulo": "Top up their wallet",
  "m.cuotaVencioHace": "Their membership expired {dias} ago.",
  "m.cuotaVenceHoy": "Their membership expires today.",
  "m.cuotaVenceEn": "Their membership expires in {dias}.",
  "m.cobrarAhora": "Charge now",
  "m.entrega": "Collection",
  "m.tocaProducto": "Tap a product to add it.",
  "m.quitar": "Remove",
  "m.ahorro": "You save {importe}",
  "m.descuentoAMano": "Manual discount −{importe}",
  "m.descuento": "Discount",
  "m.quitarDescuento": "Remove the discount",
  "m.hacerDescuento": "Apply a discount",
  "m.superaLimiteCorto": "Over what's left ({disponible}).",
  "m.registrando": "Recording...",
  "m.registrar": "Record collection · {total}",
  "m.irACobrar": "Go to payment",
  "m.falloRegistrar": "Couldn't record the collection",
  "m.superaLimite": "Over what's left: they can collect {disponible} and you've asked for {pedido}",
  "m.soloPropietario": "Only a manager or above can authorise going over the limit",

  "m.sinFichar.titulo": "They haven't checked in",
  "m.sinFichar.texto": "isn't checked in at the club. You can check them in now and carry on with the collection.",
  "m.sinFichar.nota":
    "If you carry on without checking in, the collection is still recorded, but the member won't count towards capacity or show in today's door log.",
  "m.sinFichar.seguir": "Carry on without checking in",
  "m.sinFichar.fichar": "Check in and carry on",
  "m.sinFichar.fichando": "Checking in...",
  "m.sinFichar.fallo": "Couldn't check them in",
  "m.limite.titulo": "This goes over the limit",
  "m.limite.autorizar": "Authorise and record",
  "m.limite.nota":
    "Only a manager or above can authorise it. The authorisation goes into the audit history with your name on it.",
  "m.venta.enCola": "Collection saved on this tablet",
  "m.venta.registrada": "Collection #{numero} recorded",
  "m.venta.quedan": "{importe} left in the wallet",

  "s.titulo": "Members",
  "s.enElRegistro": "{socios} in the register",
  "s.exportar": "Export CSV",
  "s.libro": "Member register",
  "s.importar": "Import CSV",
  "s.carnets": "Membership cards",
  "s.nuevo": "+ New member",
  "s.falloExportar": "Couldn't export",
  "s.buscar": "Search by name, number or ID...",
  "s.todosLosEstados": "All statuses",
  "s.cualquierTipo": "Any type",
  "s.hanVenidoDesde": "Visited since",
  "s.sinCoincidencias": "No members match that search.",
  "s.sinSocios": "No members yet.",
  "s.darDeAlta": "Sign up the first one",
  "s.oImportar": "import the ones you already have",
  "s.numero": "No.",
  "s.socio": "Member",
  "s.documento": "ID",
  "s.estado": "Status",
  "s.cuotaHasta": "Fee until",
  "s.ultimaVisita": "Last visit",
  "s.aportado": "Contributed",
  "s.cartera": "Wallet",
  "s.anios": "{anios} years old",
  "s.sinTelefono": "no phone",
  "s.queda": "{dias} left",
  "s.quedan": "{dias} left",
  "s.nunca": "Never",
  "s.hoy": "today",
  "s.ayer": "yesterday",
  "s.haceDias": "{dias} days ago",
  "s.haceUnMes": "a month ago",
  "s.haceMeses": "{meses} months ago",
  "s.haceUnAnio": "a year ago",
  "s.haceAnios": "{anios} years ago",

  "b.marcador": "Search for a member by name, number or ID...",
  "b.escanear": "Scan the member's card",
  "b.sinCoincidencias": "No member matches ‘{texto}’",
  "b.codigoDesconocido": "That code doesn't match any member of this club.",
  "b.falloCodigo": "Couldn't check the code. Try again.",
  "b.escanearBoton": "Scan",
  "b.qr.sinCamara": "This browser won't give access to the camera. Type the member number by hand.",
  "b.qr.sinPermiso":
    "You haven't given permission to use the camera. You can grant it from the padlock in the address bar.",
  "b.qr.sinDispositivo": "No camera found on this device.",
  "b.qr.falloCamara": "Couldn't open the camera. Type the member number by hand.",
  "b.qr.apunta": "Point at the member's code. It identifies them as soon as it reads it.",
  "m.productos": "{n} products",
  "m.unProducto": "1 product",

  "c.titulo": "Till",
  "c.descripcion": "Till shifts and cash counts",
  "c.entradaSalida": "Cash in / out",
  "c.cerrarCaja": "Close the till",
  "c.abrirCaja": "Open the till",
  "c.sinCaja":
    "You haven't got a till session open. While it stays closed, cash you take won't go into any cash count.",
  "c.otraAbierta": "There's another till open in the club, and what you take doesn't go into it.",
  "c.otrasAbiertas": "There are another {n} tills open in the club, and what you take doesn't go into them.",
  "c.fondoInicial": "Float",
  "c.abiertaALas": "Opened at {hora}",
  "c.dispensaciones": "Dispensing",
  "c.barra": "Bar",
  "c.cuotasYRecargas": "Fees and top-ups",
  "c.gastos": "Expenses",
  "c.deberiaHaber": "Should be in the till",
  "c.formula": "Float + cash in − cash out",
  "c.movimientos": "Movements this shift",
  "c.sinMovimientos": "No movements in this shift yet.",
  "c.hora": "Time",
  "c.tipo": "Type",
  "c.concepto": "Description",
  "c.usuario": "User",
  "c.importe": "Amount",
  "c.historial": "Shift history",

  "c.fa.titulo": "Cash outside any count",
  "c.fa.explicacion":
    "Cash was taken with no till open {cuantos} times, {importe} in all: that money is in the drawer and doesn't go into any shift's count. It happens when you take cash with the till closed, and with offline collections that go up after the shift that made them has ended.",
  "c.fa.explicacionUno":
    "One cash payment of {importe} was taken with no till open: that money is in the drawer and doesn't go into any shift's count. It happens when you take cash with the till closed, and with offline collections that go up after the shift that made them has ended.",
  "c.fa.cuando": "When",
  "c.fa.que": "What",
  "c.fa.importe": "Amount",
  "c.fa.hayMas": "Only the 100 most recent are shown.",
  "c.fa.tipo.DISPENSACION": "Collection",
  "c.fa.tipo.BARRA": "Bar",
  "c.fa.tipo.CUOTA": "Fee",
  "c.fa.tipo.DONACION": "Donation",
  "c.fa.tipo.RECARGA": "Top-up",
  "c.fa.tipo.GASTO": "Expense",
  "c.sinTurnos": "No shift has been closed yet.",
  "c.apertura": "Opened",
  "c.cierre": "Closed",
  "c.responsable": "In charge",
  "c.teorico": "Expected",
  "c.contado": "Counted",
  "c.diferencia": "Difference",
  "c.abierta": "Open",
  "c.cuadra": "Balanced",
  "c.abrir.titulo": "Open the till",
  "c.abrir.fondo": "Opening float (credits)",
  "c.abrir.fondoAyuda": "The money the shift starts with.",
  "c.abrir.notas": "Notes",
  "c.abrir.notasEjemplo": "Afternoon shift...",
  "c.abrir.boton": "Open",
  "c.abrir.fallo": "Couldn't open the till",
  "c.cerrar.titulo": "Close the till",
  "c.cerrar.segunSistema": "By the system's count there should be",
  "c.cerrar.contado": "Money counted in the drawer (credits)",
  "c.cerrar.descuadre": "Out by",
  "c.cerrar.sobra": "(money over).",
  "c.cerrar.falta": "(money missing).",
  "c.cerrar.cuadra": "The till balances exactly.",
  "c.cerrar.observaciones": "Notes on the close",
  "c.cerrar.boton": "Close shift",
  "c.cerrar.fallo": "Couldn't close the till",
  "c.mov.titulo": "Till movement",
  "c.mov.tipo": "Type",
  "c.mov.salida": "Cash out",
  "c.mov.entrada": "Cash in",
  "c.mov.nota1": "For an invoice or a purchase, use",
  "c.mov.gastos": "Expenses",
  "c.mov.nota2": ": besides coming out of the till, it gets categorised.",
  "c.mov.importe": "Amount (credits)",
  "c.mov.concepto": "Description",
  "c.mov.conceptoEjemplo": "Buying supplies...",
  "c.mov.boton": "Record",
  "c.mov.fallo": "Couldn't record the movement",
  "movimiento.ENTRADA": "Cash in",
  "movimiento.SALIDA": "Cash out",
  "movimiento.DISPENSACION": "Dispensing",
  "movimiento.MERMA": "Waste",
  "movimiento.GASTO": "Expense",

  "i.titulo": "Inventory",
  "i.descripcion": "Stock in, waste and stock counts",
  "i.almacen": "Stock",
  "i.movimientos": "Movements",
  "i.exportar": "Export CSV",
  "i.falloExportar": "Couldn't export",
  "i.enElMostrador": "At the counter",
  "i.valor": "Inventory value",
  "i.valorPie": "At catalogue contributions",
  "i.coste": "Cost of what's left",
  "i.costePie": "What was paid for it",
  "i.avisos": "Alerts",
  "i.avisosPie": "{bajos} below minimum · {caducan} expiring soon",
  "i.porCaducar": "Batches expiring soon:",
  "i.buscar": "Search for a product or category...",
  "i.nProductos": "{n} products",
  "i.unProducto": "1 product",
  "i.sinProductos": "No products. Create them in the catalogue first.",
  "i.producto": "Product",
  "i.categoria": "Category",
  "i.unidad": "Unit",
  "i.enVenta": "Counter",
  "i.guardado": "Storage",
  "i.total": "Total",
  "i.minimo": "Minimum",
  "i.lotes": "Batches",
  "i.caduca": "Expires",
  "i.valorColumna": "Value",
  "i.costeColumna": "Cost",
  "i.bajoMinimo": "below minimum",
  "i.inactivo": "Inactive",
  "i.entrada": "Stock in",
  "i.merma": "Waste",
  "i.ajuste": "Adjustment",
  "i.ultimos": "Latest movements",
  "i.sinRegistros": "No movements recorded.",
  "i.fecha": "Date",
  "i.tipo": "Type",
  "i.cantidad": "Quantity",
  "i.op.entrada": "Stock in for {producto}",
  "i.op.merma": "Waste for {producto}",
  "i.op.ajuste": "Adjustment for {producto}",
  "i.op.stockActual": "Current stock:",
  "i.op.cantidad": "Quantity",
  "i.op.stockContado": "Actual stock counted",
  "i.op.ajusteAyuda": "We'll record the difference against the system stock.",
  "i.op.codigoLote": "Batch code",
  "i.op.codigoAyuda": "Leave it blank and we'll generate one.",
  "i.op.costeTotal": "Total cost (credits)",
  "i.op.proveedor": "Source / supplier",
  "i.op.caducidad": "Expiry",
  "i.op.motivo": "Reason",
  "i.op.motivoAyuda": "It's kept in the history.",
  "i.op.registrar": "Record",
  "i.op.fallo": "Couldn't record that",
  "i.gd.titulo": "Storage · {producto}",
  "i.gd.enVenta": "On the counter:",
  "i.gd.guardado": "In storage:",
  "i.gd.queSeHace": "What to do",
  "i.gd.aVenta": "Move from storage to the counter",
  "i.gd.aGuardado": "Move from the counter to storage",
  "i.gd.comoMucho": "{cantidad} at most.",
  "i.gd.motivoAyuda": "Optional. It stays in the stock movement history.",
  "i.gd.moviendo": "Moving...",
  "i.gd.sacar": "Move to the counter",
  "i.gd.fallo": "Couldn't move it",
  "g.guardando": "Saving...",
  "i.guardadosPie": "{cantidad} in storage",

  "ba.titulo": "Bar",
  "ba.descripcion": "Coffees, soft drinks and everything that isn't dispensing",
  "ba.carta": "Bar menu",
  "ba.vacia": "The bar menu is empty.",
  "ba.vaciaAdmin": " Add what you sell under ‘Bar menu’.",
  "ba.vaciaSocio": " Ask an owner or manager to set it up.",
  "ba.sinExistencias": "Out of stock",
  "ba.agotado": "Sold out",
  "ba.quedan": "{n} left",
  "ba.cobro": "Sale",
  "ba.tocaProducto": "Tap a product to start.",
  "ba.cadaUno": "{importe} each",
  "ba.vaciar": "Clear",
  "ba.cobrando": "Taking payment...",
  "ba.cobrar": "Charge {total}",
  "ba.faltaSocio": "Pick the member to charge their wallet.",
  "ba.hoy": "Bar today",
  "ba.unCobro": "1 sale",
  "ba.cobros": "{n} sales",
  "ba.ultimos": "Latest sales",
  "ba.sinCobros": "No bar sales yet today.",
  "ba.anulada": "Void",
  "ba.falloCobrar": "Couldn't take the payment",
  "ba.cobrado": "Charged {importe} · bar receipt {numero}",
  "ba.c.nombre": "Name",
  "ba.c.nombreEjemplo": "White coffee",
  "ba.c.grupo": "Group",
  "ba.c.grupoAyuda": "To group them on the screen.",
  "ba.c.grupoPorDefecto": "Drinks",
  "ba.c.precio": "Price (credits)",
  "ba.c.existencias": "Stock",
  "ba.c.existenciasAyuda": "Only if you want to keep count.",
  "ba.c.llevarCuenta": "Keep count of the stock",
  "ba.c.llevarCuentaNota": "Coffee from the machine doesn't need counting; a box of cans does.",
  "ba.c.anadir": "Add to the menu",
  "ba.c.producto": "Product",
  "ba.c.precioColumna": "Price",
  "ba.c.quedan": "{n} left",
  "ba.c.quitar": "Remove",
  "ba.c.reponer": "Restock",
  "ba.c.fallo": "Couldn't save",

  "d.titulo": "Dispensing and bar",
  "d.descripcion": "Everything that's gone out, receipt by receipt",
  "d.a": "to",
  "d.exportar": "Export CSV",
  "d.falloExportar": "Couldn't export",
  "d.dispensario": "Dispensary",
  "d.barra": "Bar",
  "d.buscar": "Search member, product or receipt number",
  "d.atendioCualquiera": "Served by anyone",
  "d.baja": " (left)",
  "d.todosLosPuestos": "All till points",
  "d.todos": "All",
  "d.soloValidos": "Valid only",
  "d.soloAnulados": "Void only",
  "d.quitarFiltros": "Clear filters",
  "d.entregas": "Collections",
  "d.cobros": "Sales",
  "d.unAnulado": "1 void",
  "d.anulados": "{n} void",
  "d.cantidad": "Quantity",
  "d.aportaciones": "Contributions",
  "d.deDescuento": "{importe} discount",
  "d.aMano": "{importe} manual",
  "d.sinCoincidencias": "No receipt matches your search.",
  "d.sinDispensaciones": "No collections in this period.",
  "d.sinCobros": "No bar sales in this period.",
  "d.numero": "#",
  "d.fecha": "Date",
  "d.socio": "Member",
  "d.productos": "Products",
  "d.articulos": "Items",
  "d.descuento": "Discount",
  "d.importe": "Amount",
  "d.atendio": "Served by",
  "d.laSubio": "Uploaded by {nombre}",
  "d.sinSocio": "No member",
  "d.anulada": "Void",
  "d.sinConexion": "Offline",
  "d.sinConexionAyuda": "Made offline and sent when the connection came back",
  "d.descuentoAMano": "manual {importe}",
  "d.ticket": "Receipt",
  "d.anular": "Void",
  "d.tope": "You're seeing the most recent sales in the period, up to {tope}. Narrow the dates to see the rest.",
  "d.ticketNumero": "Receipt #{numero}",
  "d.imprimir": "Print",
  "d.an.tituloDispensacion": "Void collection #{numero}",
  "d.an.tituloVenta": "Void sale #{numero}",
  "d.an.dispensario": "{cantidad} goes back into stock.",
  "d.an.dispensarioCartera": "{cantidad} goes back into stock and {importe} goes back to the member's wallet.",
  "d.an.dispensarioCaja": "{cantidad} goes back into stock and {importe} is recorded as cash out of the till.",
  "d.an.barra": "The units go back into the bar stock.",
  "d.an.barraCartera": "The units go back into the bar stock and {importe} goes back to the member's wallet.",
  "d.an.barraCaja": "The units go back into the bar stock and {importe} is recorded as cash out of the till.",
  "d.an.motivo": "Reason for voiding",
  "d.an.anular": "Void",
  "d.an.fallo": "Couldn't void it",

  "tk.preparando": "Getting the receipt ready...",
  "tk.ticket": "Receipt #{numero}",
  "tk.socio": "Member #{numero}",
  "tk.atiende": "Served by",
  "tk.anulada": "*** VOID ***",
  "tk.unidades": "{cantidad} x {precio}",
  "tk.gramos": "{cantidad} g x {precio}/g",
  "tk.catalogo": "{texto} · catalogue {importe}",
  "tk.total": "TOTAL",
  "tk.descuentoAplicado": "Discount applied",
  "tk.descuento": "Discount",
  "tk.descuentoCon": "Discount {texto}",
  "tk.totalDispensado": "Total dispensed",
  "tk.formaDePago": "Payment method",
  "tk.saldoRestante": "Balance left",
  "tk.documentoInterno": "Internal association document. Not an invoice.",

  "nav.gestionDe": "Running the association",
  "nav.inicio": "Home",
  "nav.mostrador": "Counter",
  "nav.grupoAsociacion": "Association",
  "nav.donaciones": "Donations",
  "nav.socios": "Members",
  "nav.caja": "Till",
  "nav.gastos": "Expenses and purchases",
  "nav.inventario": "Inventory",
  "nav.recuentos": "Stock counts",
  "nav.barra": "Bar",
  "nav.dispensaciones": "Dispensing",
  "nav.descuentos": "Discounts",
  "nav.grupoAjustes": "Settings",
  "nav.informes": "Reports",
  "nav.ajustes": "Settings",
  "nav.dias": "Opening days",
  "nav.contrato": "Agreement",
  "nav.accesos": "Check-ins",
  "nav.renovaciones": "Renewals",
  "nav.cuotas": "Fees",
  "nav.comunidad": "Community",
  "nav.catalogo": "Catalogue",
  "nav.cartas": "Menus",
  "nav.contrasena": "Password",
  "rol.PROPIETARIO": "Owner",
  "rol.TRABAJADOR": "Staff",
  "nav.pw.titulo": "Change my password",
  "nav.pw.hecho": "Password changed. Use it next time you sign in.",
  "nav.pw.actual": "Current password",
  "nav.pw.nueva": "New password",
  "nav.pw.minimo": "At least 8 characters.",
  "nav.pw.repetir": "Repeat the new one",
  "nav.pw.noCoinciden": "They don't match.",
  "nav.pw.cambiar": "Change",
  "nav.pw.fallo": "Couldn't change the password",

  "p.buenosDias": "Good morning",
  "p.buenasTardes": "Good afternoon",
  "p.buenasNoches": "Good evening",
  "p.saludo": "{saludo}, {nombre}",
  "p.descripcion": "Club activity at a glance",
  "p.nuevaDispensacion": "New collection",
  "p.sinCaja": "No till session is open. Cash payments won't show up in the cash count.",
  "p.abrirCaja": "Open the till",
  "p.unaSolicitud": "1 application is waiting for an answer.",
  "p.solicitudes": "{n} applications are waiting for an answer.",
  "p.verSolicitudes": "See applications",
  "p.unaCuotaVencida": "1 member with an expired membership.",
  "p.cuotasVencidas": "{n} members with an expired membership.",
  "p.unaPorVencer": "1 membership expires in the next 30 days.",
  "p.porVencer": "{n} memberships expire in the next 30 days.",
  "p.verRenovaciones": "See renewals",
  "p.pocoStockDe": "{producto} is running low.",
  "p.bajoMinimo": "{n} products are below the minimum stock.",
  "p.verInventario": "See inventory",
  "p.dispensadoHoy": "Dispensed today",
  "p.unaEntrega": "1 collection",
  "p.entregas": "{n} collections",
  "p.unSocio": "1 member",
  "p.socios": "{n} members",
  "p.entregasASocios": "{entregas} to {socios}",
  "p.aportacionesHoy": "Contributions today",
  "p.cajaAbierta": "Till open",
  "p.sinCajaAbierta": "No till session",
  "p.barraHoy": "Bar today",
  "p.sociosActivos": "Active members",
  "p.enTotal": "{n} in total",
  "p.esteMes": "This month",
  "p.enEntregas": "{importe} across {entregas}",
  "p.masDispensado": "Most dispensed today",
  "p.nadaHoy": "Nothing's gone out today yet.",
  "p.producto": "Product",
  "p.entregasColumna": "Collections",
  "p.cantidad": "Quantity",
  "p.importe": "Amount",
  "p.masBarra": "Best sellers at the bar today",
  "p.nadaBarra": "Nothing from the bar today.",
  "p.cobrosColumna": "Sales",
  "p.unidades": "Units",
  "p.ultimas": "Latest collections",
  "p.sinDispensaciones": "No collections recorded yet.",
  "p.socio": "Member",
  "p.fecha": "Date",
  "p.anulada": "Void",
  "p.primerosPasos": "First steps",
  "p.recienCreado": "The club's just been created. Here's what to set up before you open the counter:",
  "p.paso.ajustes": "Check the settings",
  "p.paso.ajustesResto": ": daily and monthly limit per member, annual fee and association details.",
  "p.paso.catalogo": "Create the strains",
  "p.paso.catalogoResto": " with their contribution per gram. The basic categories are already there.",
  "p.paso.stock": "Enter the opening stock",
  "p.paso.stockResto": " with one entry per batch of each strain.",
  "p.paso.socio": "Sign up the first member",
  "p.paso.socioResto": " and take their fee.",
  "p.paso.caja": "Open the till",
  "p.paso.cajaResto": " at the start of the shift so the cash adds up.",
  "p.estadoSocios": "Membership status",
  "p.stockBajo": "Low stock",

  "il.buscarSocio": "Find a member",
  "il.nuevoSocio": "New member",
  "il.dispensar": "Dispense",
  "il.barra": "Bar",
  "il.dentroAhora": "Inside now",
  "il.aforoCompleto": "At capacity",
  "il.hanPasadoHoy": "In today",
  "il.solicitudes": "Applications",
  "il.esperando": "waiting",
  "il.anotarGasto": "Log an expense",

  "pp.paraEmpezar": "To get started",
  "pp.enEsteOrden": "This order is the quickest. It disappears on its own once you're up and running.",
  "pp.hechosDe": "{hechos} of {total}",
  "pp.ir": "Go",
  "pasos.datos.titulo": "Fill in the club's details",
  "pasos.datos.detalle": "Name, CIF and address. They show on receipts and in the member register.",
  "pasos.productos.titulo": "Build your catalogue",
  "pasos.productos.detalle": "The categories are already set up. Add what you dispense, with its contribution.",
  "pasos.stock.titulo": "Enter the stock you have",
  "pasos.stock.detalle": "One entry per batch, with supplier and cost. That's where traceability comes from.",
  "pasos.socios.titulo": "Add your members",
  "pasos.socios.detalle": "One by one, or by importing the CSV from whatever you used before.",
  "pasos.equipo.titulo": "Add your team",
  "pasos.equipo.detalle": "Each person with their own account and role: that way the log says who did what.",
  "pasos.caja.titulo": "Open the day's till session",
  "pasos.caja.detalle":
    "With the float. When you close it, the cash count compares what you counted with what should be there.",

  "as.soloLectura": "The software is read-only.",
  "as.llevaSinRegularizar":
    "The subscription has been unpaid for {dias}. You can still look at everything and download the backups from Settings, but you can't record anything new. As soon as the payment goes through, it all comes back straight away.",
  "as.vencioAyer": "The subscription expired yesterday.",
  "as.vencioHace": "The subscription expired {dias} ago.",
  "as.siSigue": "Nothing changes for now. If it stays unpaid, the software will go read-only.",
  "as.venceHoy": "The subscription expires today{fecha}.",
  "as.venceManana": "The subscription expires tomorrow{fecha}.",
  "as.venceEn": "The subscription expires in {dias}{fecha}.",
  "as.entreParentesis": " ({fecha})",
  "as.noHayQueHacer": "Nothing to do here: the date updates itself as soon as the payment goes through.",

  "bl.explicacion": "The screen locked itself after sitting idle. Type your password to carry on where you left off.",
  "bl.tuContrasena": "Your password",
  "bl.comprobando": "Checking...",
  "bl.continuar": "Continue",
  "bl.salirOtraCuenta": "Sign out and sign in with another account",
  "bl.fallo": "Couldn't check the password",

  "ec.sinConexion": "No connection to the server.",
  "ec.sinConexionDesde": "No connection to the server since {hora}.",
  "ec.conPaquete":
    "The counter keeps working from what's stored on this tablet: collections are recorded here and go up on their own once the network's back.",
  "ec.sinPaquete": "This tablet hasn't got the catalogue stored: you can't dispense until the network is back.",
  "ec.unaPendiente": "1 collection waiting to be sent.",
  "ec.pendientes": "{n} collections waiting to be sent.",
  "ec.unaRechazada": "1 rejected by the server.",
  "ec.rechazadas": "{n} rejected by the server.",
  "ec.hayRechazada": "There's 1 collection the server hasn't accepted: take a look.",
  "ec.hayRechazadas": "There are {n} collections the server hasn't accepted: take a look.",
  "ec.verCola": "See the queue ({n})",
  "ec.enviando": "Sending...",
  "ec.enviarAhora": "Send now",
  "ec.titulo": "Collections stored on this tablet",
  "ec.explicacion":
    "They were made offline and send themselves once the network is back, in this order. The server charges them using its own amounts and records anything they broke (limit, stock, balance) as an issue on the collection.",
  "ec.nadaPorEnviar": "Nothing left to send.",
  "ec.cuando": "When",
  "ec.socio": "Member",
  "ec.que": "What",
  "ec.importe": "Amount",
  "ec.estado": "Status",
  "ec.rechazada": "Rejected: {motivo}",
  "ec.reintentar": "Retry",
  "ec.descartar": "Discard",
  "ec.confirmarDescartar": "Discard this collection? It won't be recorded anywhere.",
  "ec.pendienteDeEnviar": "Waiting to be sent",
  "ec.unIntento": " (1 attempt)",
  "ec.intentos": " ({n} attempts)",

  "gi.titulo": "Where the money comes from",
  "gi.dias7": "7 days",
  "gi.dias30": "30 days",
  "gi.meses12": "12 months",
  "gi.dispensacion": "Dispensing",
  "gi.barra": "Bar",
  "gi.cuotas": "Fees",
  "gi.sinIngresos": "No income recorded in this period yet.",

  "tb.dejarRecadoEquipo": "Leave a note for the team",
  "tb.titulo": "Team noticeboard",
  "tb.dejarRecado": "Leave a note",
  "tb.ejemplo": "The product arrives Thursday morning...",
  "tb.fijarArriba": "Pin to top",
  "tb.poniendo": "Posting...",
  "tb.poner": "Post",
  "tb.alguien": "Someone",
  "tb.quitar": "Take it down",

  "dc.titulo": "Charge {socio}'s membership fee",
  "dc.cobrando": "Charging...",
  "dc.cobrar": "Charge",
  "dc.importe": "Amount (credits)",
  "dc.periodo": "Period",
  "dc.unAnio": "1 year",
  "dc.dosAnios": "2 years",
  "dc.tresAnios": "3 years",
  "dc.formaDePago": "Payment method",
  "dc.carteraDelSocio": "Member's wallet",
  "dc.nota":
    "If they've still got time left, the new period tacks on to the end; if it's expired, it counts from today.",
  "dc.fallo": "Couldn't charge the membership fee",

  "dr.titulo": "Top up {socio}'s wallet",
  "dr.ingresar": "Top up {importe}",
  "dr.tieneAhora": "Balance now",
  "dr.leFaltan": "They're {importe} short for what they're collecting.",
  "dr.importe": "Amount (credits)",
  "dr.formaDePago": "Payment method",
  "dr.entraEnCaja": "Goes into the shift's till.",
  "dr.noTocaCaja": "Doesn't touch the till.",
  "dr.seQuedaraCon": "Will be left with",
  "dr.noEsIngreso": "This money isn't income for the association.",
  "dr.esDelSocio": "It's the member's until they spend it, and they can ask for it back.",
  "dr.confirmarTitulo": "Have you taken the money?",
  "dr.todaviaNo": "Not yet",
  "dr.ingresando": "Topping up...",
  "dr.siIngresar": "Yes, top up",
  "dr.vasASumar": "You're adding {importe} to {socio}'s wallet, leaving it at {total}.",
  "dr.confirmoEfectivo": "I confirm I've taken {importe} in cash",
  "dr.confirmo": "I confirm I've taken {importe}",
  "dr.siTeEquivocas":
    "If you get it wrong you can sort it out from the member's record, refunding the balance or adjusting it, and both leave a trace. But it's far easier not to get it wrong now.",
  "dr.fallo": "Couldn't top up",

  "cc.entregado": "Received",
  "cc.aCobrar": "To pay",
  "cc.cambio": "Change",
  "cc.faltan": "{importe} short",
  "cc.oEscribelo": "Or type it",
  "cc.poner": "Set",
  "cc.empezarDeNuevo": "Start again",

  "fp.firmado": "Signed.",
  "fp.estaBien": "Is it right? Confirm it.",
  "fp.firmaAqui": "Sign here with your finger or the mouse.",
  "fp.repetirTitulo": "Sign again",
  "fp.repetir": "Redo",
  "fp.confirmar": "Confirm",

  "ci.sinCamara": "This browser won't give access to the camera. Upload the image from a file.",
  "ci.sinPermiso":
    "You haven't given permission to use the camera. You can grant it from the padlock in the address bar.",
  "ci.sinDispositivo": "No camera found. Upload the image from a file.",
  "ci.falloCamara": "Couldn't open the camera. Upload the image from a file.",
  "ci.obligatoria": "Required",
  "ci.saltada": "Skipped",
  "ci.hacerFoto": "Take photo",
  "ci.repetir": "Retake",
  "ci.camara": "Camera",
  "ci.subir": "Upload",
  "ci.quitar": "Remove",

  "cs.titulo": "Membership agreement",
  "cs.firmoEl": "Signed version {numero} on {fecha}.",
  "cs.firmo": "Signed version {numero}.",
  "cs.sinFirmar": "Not signed. Below is today's one (version {numero}) with their details, ready to print.",
  "cs.verEImprimir": "View and print",
  "cs.apuntando": "Recording...",
  "cs.marcarFirmado": "Mark as signed",
  "cs.tambienPortal": "They can also accept it themselves from their portal, and then it's recorded on its own.",
  "cs.verContratoClub": "View the club's agreement",
  "cs.modalTitulo": "Agreement · member no. {numero}",
  "cs.noFirmadoAun": "They haven't signed it yet: this is the document to take to the counter.",
  "cs.firmaDelSocio": "Member's signature",
  "cs.firmaYSello": "Association's signature and stamp",
  "cs.fallo": "Couldn't record the signature",

  "sn.titulo": "New member",
  "sn.descripcion": "Nothing's saved until the end: until then you can go back and forth",
  "sn.paso.avalista": "Sponsor",
  "sn.paso.datos": "Details",
  "sn.paso.documento": "ID",
  "sn.paso.contrato": "Agreement",
  "sn.paso.cuota": "Fee",
  "sn.paso.repaso": "Review",
  "sn.falta.nombre": "Type the first name",
  "sn.falta.apellidos": "Type the surname",
  "sn.falta.nacimiento": "The date of birth is missing",
  "sn.falta.documento": "The ID number is missing",
  "sn.falta.foto": "The member's photo is missing",
  "sn.falta.frente": "The front of the ID is missing",
  "sn.falta.reverso": "The back of the ID is missing, or tick that there isn't one",
  "sn.quienAvala": "Who's sponsoring them?",
  "sn.avalistaNota": "Plenty of club rules ask for a member to vouch for whoever joins. If yours don't, skip it.",
  "sn.quitar": "Remove",
  "sn.quienEs": "Who they are",
  "sn.nombre": "First name",
  "sn.apellidos": "Surname",
  "sn.nacimiento": "Date of birth",
  "sn.consumo": "Expected consumption (g/month)",
  "sn.consumoAyuda": "The member declares it; it goes in the agreement.",
  "sn.telefono": "Phone",
  "sn.email": "Email",
  "sn.dondeVive": "Where they live",
  "sn.direccion": "Address",
  "sn.ciudad": "Town",
  "sn.cp": "Postcode",
  "sn.numeroSocio": "Member number",
  "sn.numeroAyuda": "Leave it empty and they get the next free number.",
  "sn.idioma": "Language",
  "sn.idiomaAyuda": "The one they see the portal in and get the fee emails in.",
  "sn.idiomaDelClub": "The club's",
  "sn.notas": "Internal notes",
  "sn.documento": "ID document",
  "sn.tipo": "Type",
  "sn.pasaporte": "Passport",
  "sn.numero": "Number",
  "sn.fotoSocio": "Member's photo",
  "sn.fotoSocioAyuda": "So you recognise them at the counter",
  "sn.docFrente": "ID, front",
  "sn.docFrenteAyuda": "The side with the photo",
  "sn.docReverso": "ID, back",
  "sn.noLoTiene": "They haven't got one",
  "sn.docReversoAyuda": "The other side",
  "sn.sinReverso": "No back side",
  "sn.fotosHacenFalta":
    "You need the photos to sign someone up: they're the only way to check later that whoever came in was who they said they were. If you haven't got a camera, you can upload them from a file.",
  "sn.loQueFirma": "What they sign",
  "sn.firma": "Signature",
  "sn.rgpd": "They've also signed the consent to data processing (GDPR)",
  "sn.sinFirmaSigue":
    "Without a signature the sign-up still goes through, but the member is left pending signature and can sign from their portal.",
  "sn.cuota": "Membership fee",
  "sn.cuotaNota": "Each membership type carries its own fee, discount and limits. You change them in Settings.",
  "sn.sinTipo": "No type",
  "sn.condicionesGenerales": "The club's general terms",
  "sn.alAnio": "a year",
  "sn.alAnioLaDelClub": "a year · the club's own",
  "sn.descuentoCatalogo": "−{porcentaje}% off the catalogue",
  "sn.sinDescuento": "No discount",
  "sn.cobrarAhora": "Charge now",
  "sn.sinCobrarla": "If you don't charge it the member stays pending and can't collect until they pay.",
  "sn.formaDePago": "Payment method",
  "sn.repaso": "Review",
  "sn.r.nombre": "Name",
  "sn.r.documento": "ID",
  "sn.r.nacimiento": "Born",
  "sn.r.consumo": "Declared consumption",
  "sn.r.gMes": "{n} g/month",
  "sn.r.avalista": "Sponsor",
  "sn.r.sinAvalista": "No sponsor",
  "sn.r.tipo": "Type",
  "sn.r.rgpd": "GDPR",
  "sn.r.firmado": "Signed",
  "sn.r.pendiente": "Pending",
  "sn.r.contrato": "Agreement",
  "sn.r.pendienteFirma": "Pending signature",
  "sn.loQueSeCobra": "What you're charging",
  "sn.cuotaAnual": "Annual fee",
  "sn.noSeCobraNada": "Nothing is charged now: the member stays pending.",
  "sn.nadaQueCobrar": "Nothing to charge.",
  "sn.seCobraEn": "Paid by {forma}.",
  "sn.mini.socio": "Member",
  "sn.mini.frente": "Front",
  "sn.mini.reverso": "Back",
  "sn.mini.firma": "Signature",
  "sn.sinFoto": "No photo",
  "sn.atras": "Back",
  "sn.creando": "Creating...",
  "sn.crear": "Create member",
  "sn.siguiente": "Next",
  "sn.fallo": "Couldn't create the member",
  "sn.avisoSubida": "The member is created, but {que} couldn't be uploaded.",
  "sn.subida.foto": "the member's photo",
  "sn.subida.frente": "the front of the ID",
  "sn.subida.reverso": "the back of the ID",
  "sn.subida.firma": "the signature",
  "sn.seAsigna": "(assigned when you create it)",

  "se.titulo": "Edit {socio}",
  "se.descripcion": "Member number {numero}",
  "se.guardarCambios": "Save changes",
  "se.datosPersonales": "Personal details",
  "se.nombre": "First name *",
  "se.apellidos": "Surname *",
  "se.tipoDocumento": "ID type",
  "se.numero": "Number *",
  "se.nacimiento": "Date of birth *",
  "se.limitesPropios": "Own limits",
  "se.sinLimites": "Leave them empty and the club's limits apply.",
  "se.sinLimitesConCifras": "Leave them empty and the club's limits apply ({diario} g a day, {mensual} g a month).",
  "se.limiteDiario": "Daily limit (g)",
  "se.limiteMensual": "Monthly limit (g)",
  "se.delClub": "Club: {cantidad}",
  "se.asociacion": "Association",
  "se.tipoDeSocio": "Membership type",
  "se.sinTipo": "No type (club's terms)",
  "se.avalista": "Sponsor",
  "se.buscarAvalista": "Search for a sponsor...",
  "se.rgpd": "Consent to data processing (GDPR) signed",
  "se.fallo": "Couldn't save the changes",

  "lb.volver": "Back to members",
  "lb.imprimir": "Print or save as PDF",
  "lb.comoPdf":
    "Printing gives you the document on its own, without the navigation. To save it as a PDF, choose “Save as PDF” as the printer destination.",
  "lb.titulo": "Member register",
  "lb.emision": "Extract issued on {fecha} by {quien}.",
  "lb.numero": "No.",
  "lb.apellidosNombre": "Surname and first name",
  "lb.documento": "ID",
  "lb.nacimiento": "Born",
  "lb.alta": "Joined",
  "lb.baja": "Left",
  "lb.avalista": "Sponsor",
  "lb.rgpd": "GDPR",
  "lb.estado": "Status",
  "lb.numeroAvalista": "no. {numero}",
  "lb.si": "Yes",
  "lb.sinSocios": "There's no one on the register yet.",
  "lb.unInscrito": "1 member on the register",
  "lb.inscritos": "{n} members on the register",
  "lb.enTotal": "in total: {reparto}.",
  "lb.enAlta": "{n} active",
  "lb.suspendidos": "{n} suspended",
  "lb.pendientes": "{n} pending",
  "lb.bajas": "{n} left",
  "lb.consentimiento": "With signed consent to data processing: {conConsentimiento} of {total}.",
  "lb.consumoDeclarado":
    "Consumption declared by active members: {alMes} a month, which is {alAnio} a year. It's what they said they needed when they joined.",
  "lb.notaPie": "Internal association document, drawn from its member register on the date shown.",

  "cn.preparando": "Preparing the membership cards...",
  "cn.falloPreparar": "Couldn't prepare the membership cards.",
  "cn.volver": "Back",
  "cn.unCarnet": "1 membership card",
  "cn.carnets": "{n} membership cards",
  "cn.cuantos": "{carnets}, at card size: eight to an A4 sheet.",
  "cn.cuantosTodos": "{carnets} (every active member), at card size: eight to an A4 sheet.",
  "cn.comoImprimir":
    "Printing gives you the sheet of cards on its own, without the navigation; to save it as a PDF, choose “Save as PDF” as the printer destination.",
  "cn.elQr": "The QR code is the same one as in the member portal: the counter reads it just the same.",
  "cn.ninguno": "There's no one to print.",
  "cn.numero": "No. {numero}",
  "cn.validoHasta": "Valid until {fecha}",
  "cn.socioDesde": "Member since {fecha}",
  "cn.documentoInterno": "Internal association document",

  "si.titulo": "Import members",
  "si.descripcion": "Bring your member register in from another program or from a spreadsheet",
  "si.volver": "Back",
  "si.importadoUno": "1 member imported. They're on the register now.",
  "si.importados": "{n} members imported. They're on the register now.",
  "si.verSocios": "View members",
  "si.otroFichero": "Import another file",
  "si.paso1": "1. Get the file ready",
  "si.explicacion":
    "A CSV with one row per member. You need at least “Nombre”, “Apellidos”, “Documento” and “Fecha nacimiento”, with the column names in Spanish or Catalan. If there's a “Número” column, each member keeps the number they've always had; if not, they get the next free ones. The rest (phone, email, address, status, join date, member until, notes) is used if it's there. The file this program exports works too.",
  "si.explicacionNombreEntero":
    "If the first name and surname come together in a single column (“Nombre y apellidos”, “Nombre completo”), I'll split them and show you the split before anything is saved.",
  "si.explicacionColumnasDobles":
    "And if it's the other way round, spread over two columns (“Primer apellido” and “Segundo apellido”, or “Primer nombre” and “Segundo nombre”), I'll join them up.",
  "si.direccion": "Address",
  "si.direccionJuntada":
    "The address came spread over several columns (“Calle”, “Número”, “Piso”…) and I've joined it into one line, putting the label your file uses in front of each part. Here it is in full.",
  "si.numeroEnLaCalle":
    "Careful with the “Número” column: as the file brings the address split up, I've read it as the street number, not the member number. Member numbers are handed out from the next free one. If that column was your member numbers, rename the door-number one (“Número calle”) and upload it again.",
  "si.explicacionVarios":
    "When there are several columns for the same thing (“Teléfono 1” and “Teléfono 2”), I keep the first: a member has one phone number on file, not three.",
  "si.explicacionDireccion":
    "The address can come split into “Calle”, “Número”, “Piso” and “Puerta”, and the DNI into number and letter: I'll join them up.",
  "si.formatos":
    "Dates as 2026-09-01 or 01/09/2026. Separator “;” or “,”. Files saved by Excel work, whatever encoding they're in.",
  "si.descargarPlantilla": "Download template",
  "si.falloPlantilla": "Couldn't download the template",
  "si.paso2": "2. Pick the file and check it",
  "si.analizarNoGuarda": "Checking saves nothing: it only looks the file over and tells you what it finds.",
  "si.analizando": "Checking...",
  "si.analizar": "Check",
  "si.paso3": "3. Result",
  "si.procesando": "Working through the file...",
  "si.eligeFichero": "Pick a file and press Check.",
  "si.filas": "Rows",
  "si.validas": "Valid",
  "si.conErrores": "With errors",
  "si.columnas": "Columns recognised: {columnas}",
  "si.corrige": "Fix these rows in the file and check it again. Nothing is imported while there are errors.",
  "si.fila": "Row",
  "si.problema": "Problem",
  "si.numero": "No.",
  "si.socio": "Member",
  "si.nombre": "First name",
  "si.apellidos": "Surname",
  "si.columnasJuntadas":
    "The file has the surname (or the first name) spread over two columns and I've joined them into one. There's nothing to guess here, it comes out exactly as it came in; have a look anyway to confirm the order is the one you expected.",
  "si.nombrePartido":
    "Your file has the first name and the surname in the same column and I've split them. No part of the name is lost, but where the first name ends and the surname starts is a guess of mine. Go over it here before importing: if any of them came out wrong, edit the member to fix it.",
  "si.documento": "ID",
  "si.estado": "Status",
  "si.seMuestran": "Showing {cuantos} of {total}.",
  "si.importarUno": "Import 1 member",
  "si.importarVarios": "Import {n} members",
  "si.falloLeer": "Couldn't read the file",
  "si.falloImportar": "Couldn't import",

  "sf.volver": "Back",
  "sf.carnet": "Membership card",
  "sf.editar": "Edit details",
  "sf.dispensar": "Dispense",
  "sf.unAnio": "1 year",
  "sf.anios": "{n} years",
  "sf.subiendo": "Uploading...",
  "sf.cambiarFoto": "Change photo",
  "sf.anadirFoto": "Add photo",
  "sf.quitarFoto": "Remove photo",
  "sf.falloQuitarFoto": "Couldn't remove the photo",
  "sf.documento": "ID",
  "sf.alta": "Joined",
  "sf.cuotaHasta": "Member until",
  "sf.avalista": "Sponsor",
  "sf.rgpd": "GDPR consent",
  "sf.portal": "Member portal",
  "sf.activo": "Active",
  "sf.sinAcceso": "No access",
  "sf.cartera": "Wallet",
  "sf.recargar": "Top up",
  "sf.cobrarCuota": "Charge fee",
  "sf.devolverSaldo": "Refund balance",
  "sf.ajustarSaldo": "Adjust balance",
  "sf.suspender": "Suspend temporarily",
  "sf.levantar": "Lift suspension",
  "sf.falloLevantar": "Couldn't lift it",
  "sf.reactivar": "Reactivate member",
  "sf.falloReactivar": "Couldn't reactivate",
  "sf.renovarPortal": "Renew portal access",
  "sf.darPortal": "Give portal access",
  "sf.falloCodigo": "Couldn't generate the code",
  "sf.retirarPortal": "Remove portal access",
  "sf.falloRetirar": "Couldn't remove the access",
  "sf.darDeBaja": "End membership",
  "sf.descargarDatos": "Download their data",
  "sf.falloDescargar": "Couldn't download the data",
  "sf.borrarDatos": "Delete their data",
  "sf.consumoYLimites": "Consumption and limits",
  "sf.hoy": "Today",
  "sf.esteMes": "This month",
  "sf.puedeRetirar": "Can collect now",
  "sf.consumoDeclarado": "Consumption declared when joining",
  "sf.alMes": "{cantidad}/month",
  "sf.tab.resumen": "Summary",
  "sf.tab.dispensaciones": "Collections",
  "sf.tab.cartera": "Activity",
  "sf.tab.cuotas": "Fees",
  "sf.entregasRegistradas": "Collections recorded",
  "sf.ultimaVisita": "Last visit",
  "sf.cuotasPagadas": "Fees paid",
  "sf.notas": "Internal notes",
  "sf.avalados": "Members they sponsor",
  "sf.sinDispensaciones": "This member hasn't collected anything yet.",
  "sf.fecha": "Date",
  "sf.productos": "Products",
  "sf.cantidad": "Quantity",
  "sf.importe": "Amount",
  "sf.anulada": "Void",
  "sf.sinCartera": "No wallet activity.",
  "sf.concepto": "Description",
  "sf.saldo": "Balance",
  "sf.sinCuotas": "No fees recorded.",
  "sf.validaHasta": "Valid until",
  "sf.codigoPortal": "Code for the member portal",
  "sf.hecho": "Done",
  "sf.daleLosDatos": "Give the member these details. The code expires in 7 days and works only once.",
  "sf.numeroDeSocio": "Member number",
  "sf.codigo": "Code",
  "sf.entraEn": "Go to {url} and choose “Activate my access”.",
  "sf.devolverTitulo": "Refund the member's balance",
  "sf.devolverEtiqueta": "Amount to refund",
  "sf.ajustarTitulo": "Adjust balance",
  "sf.ajustarEtiqueta": "Adjustment amount (negative to take off)",
  "sf.bajaTitulo": "End the member's membership",
  "sf.bajaTexto":
    "The member won't be able to collect any more. The history is kept. If there's money left in the wallet, refund it first.",
  "sf.suspenderTitulo": "Suspend temporarily",
  "sf.suspenderTexto":
    "While they're suspended they can't check in or collect anything. You lift it from this same record.",
  "sf.suspenderBoton": "Suspend",
  "sf.comoMaximo": "{importe} credits at most.",
  "sf.confirmar": "Confirm",
  "sf.motivoObligatorio": "Reason (required)",
  "sf.motivo": "Reason",
  "sf.formaDePago": "Payment method",
  "sf.falloAccion": "Couldn't finish that",
  "sf.borrarTitulo": "Delete the member's data",
  "sf.borrando": "Deleting...",
  "sf.sinVuelta": "There's no going back.",
  "sf.seBorra": "Deleted:",
  "sf.seBorraLista":
    "first name, surname, ID, date of birth, phone, email, address, photo, notes and their portal access.",
  "sf.seConserva": "Kept:",
  "sf.seConservaLista":
    "the member number and their collections, fees, wallet activity and check-ins. They're the club's accounts and traceability, and deleting them would leave the books not adding up and the batch trail broken. They stay behind with no name on them.",
  "sf.copiasDeSeguridad":
    "Backups made before today still hold their data: it goes when they expire. If you need to go further than that, talk it over with your adviser.",
  "sf.escribeNumero": "Type the member number ({numero}) to confirm",
  "sf.falloBorrar": "Couldn't delete the data",
  "sf.frente": "Front",
  "sf.reverso": "Back",
  "sf.sinFoto": "No photo",
  "sf.cambiar": "Change",
  "sf.subir": "Upload",

  "cu.titulo": "Fees",
  "cu.descripcion": "What's been charged in fees, and the membership types you have",
  "cu.a": "to",
  "cu.exportar": "Export CSV",
  "cu.falloExportar": "Couldn't export",
  "cu.tabCobradas": "Charged",
  "cu.tabTipos": "Membership types",
  "cu.cobradoEnPeriodo": "Charged in the period",
  "cu.unaCuota": "1 fee",
  "cu.cuotas": "{n} fees",
  "cu.altas": "Joins",
  "cu.altasPie": "First fees: new members",
  "cu.enEfectivo": "In cash",
  "cu.enEfectivoPie": "Goes into the shift's till",
  "cu.buscar": "Search by member, ID or description",
  "cu.altasYRenovaciones": "Joins and renewals",
  "cu.soloAltas": "Joins only",
  "cu.soloRenovaciones": "Renewals only",
  "cu.cualquierTipo": "Any membership type",
  "cu.cualquierForma": "Any payment method",
  "cu.cualquierPuesto": "Any till point",
  "cu.sinCoincidencias": "No fees charged with these filters. To see who's due to pay,",
  "cu.miraRenovaciones": "have a look at Renewals",
  "cu.fecha": "Date",
  "cu.socio": "Member",
  "cu.tipoDeSocio": "Membership type",
  "cu.altaORenovacion": "Join or renewal",
  "cu.periodo": "Period",
  "cu.forma": "Method",
  "cu.puesto": "Till point",
  "cu.cobro": "Charged by",

  "cu.an.boton": "Void",
  "cu.an.anulada": "Voided",
  "cu.an.titulo": "Void a {importe} fee",
  "cu.an.queVaAPasar":
    "{socio} gets {importe} back and the fee stops counting: the date they're paid up to goes back to their previous fee, and if this was the only one they won't be able to collect anything until they pay another.",
  "cu.an.deLaCartera": "They paid from their wallet, so the balance goes straight back.",
  "cu.an.delCajon":
    "They paid cash, so the money comes out of the drawer it went into. If that shift has already closed, no till is touched: its count was signed off with this fee inside.",
  "cu.an.motivo": "Reason",
  "cu.an.motivoAyuda":
    "It gets recorded. Six months from now it's the only thing that will explain this.",
  "cu.an.confirmar": "Void the fee",
  "cu.an.anulando": "Voiding…",
  "cu.an.fallo": "Couldn't void the fee",
  "cu.importe": "Amount",
  "cu.alta": "Join",
  "cu.renovacion": "Renewal",
  "cu.tiposTitulo": "Membership types",
  "cu.tiposNota": "Each type carries its own fee, how long it lasts, its discount and its limits.",
  "cu.seEditanEnAjustes": "You create and edit them in Settings",
  "cu.sinTipos": "No membership types. Everyone pays the club's standard fee.",
  "cu.sinTiposConCuota": "No membership types. Everyone pays the club's standard fee, {importe} a year.",
  "cu.tipo": "Type",
  "cu.cuota": "Fee",
  "cu.dura": "Lasts",
  "cu.descuento": "Discount",
  "cu.socios": "Members",
  "cu.apagado": " · off",
  "cu.laDelClub": "the club's",
  "cu.unAnio": "1 year",
  "cu.anios": "{n} years",
  "cu.unMes": "1 month",
  "cu.meses": "{n} months",

  "rn.titulo": "Renewals",
  "rn.descripcion": "Members whose membership has expired or is about to",
  "rn.vencenEnDias": "Expiring in {dias}",
  "rn.cuotaVencida": "Expired",
  "rn.noPuedenRetirar": "They can't collect anything",
  "rn.convieneAvisar": "Worth reminding them",
  "rn.cuotaAnual": "Annual fee",
  "rn.nadieVencida": "Nobody's membership has expired.",
  "rn.vencenEnLosProximos": "Expiring in the next {dias}",
  "rn.ningunaVence": "No membership expires in the next {dias}.",
  "rn.laAsociacion": "the association",
  "rn.cobrada": "Fee charged for {socio}. Valid until {fecha}.",
  "rn.socio": "Member",
  "rn.telefono": "Phone",
  "rn.estado": "Status",
  "rn.cuotaHasta": "Fee until",
  "rn.whatsapp": "WhatsApp",
  "rn.mensajeWhatsapp":
    "Hi {nombre}, this is {club}. Your membership fee is due for renewal. Pop into the club whenever you can. Thanks.",
  "rn.sinCuota": "No fee",
  "rn.vencioHace": "expired {dias} ago",
  "rn.venceHoy": "expires today",
  "rn.queda": "{dias} left",
  "rn.quedan": "{dias} left",
  "rn.cobrarCuota": "Charge fee",
  "rn.nadieAQuienAvisar":
    "Nobody to remind: either they've been reminded about this one already, or it hasn't expired yet.",
  "rn.avisadoUno": "1 member reminded ({porCorreo} by email, {porMovil} on their phone).",
  "rn.avisados": "{n} members reminded ({porCorreo} by email, {porMovil} on their phone).",
  "rn.unoSinCorreo": "1 member with no email on file.",
  "rn.sinCorreo": "{n} members with no email on file.",
  "rn.falloRecordatorios": "Couldn't send the reminders",
  "rn.enviando": "Sending...",
  "rn.avisar": "Send reminders",

  "gc.gastos": "Expenses",
  "gc.compras": "Purchases",
  "gc.proveedores": "Suppliers",

  "pago.DOMICILIADO": "Direct debit",

  "ga.titulo": "Expenses and purchases",
  "ga.descripcion": "What goes out of the association, with its category",
  "ga.a": "to",
  "ga.categorias": "Categories",
  "ga.exportar": "Export CSV",
  "ga.falloExportar": "Couldn't export",
  "ga.nuevo": "+ Expense",
  "ga.totalFiltrado": "Filtered total",
  "ga.totalPeriodo": "Total for the period",
  "ga.unApunte": "1 entry",
  "ga.apuntes": "{n} entries",
  "ga.enEfectivo": "In cash",
  "ga.saleDeCaja": "Comes out of the day's till",
  "ga.categoriaMayor": "Biggest category",
  "ga.porCategoria": "By category",
  "ga.sinGastos": "No expenses in this period.",
  "ga.gastosDe": "{categoria} expenses",
  "ga.todosLosGastos": "All expenses",
  "ga.buscar": "Search by description, supplier or note",
  "ga.todasCategorias": "All categories",
  "ga.apagada": " (off)",
  "ga.cualquierForma": "Any payment method",
  "ga.cualquierPuesto": "Any till point",
  "ga.conPagosProveedor": "Including supplier payments",
  "ga.verAnulados": "Show the voided ones",
  "ga.sinCoincidencias": "No expense matches those filters.",
  "ga.sinApuntes": "No expenses recorded in this period.",
  "ga.numero": "#",
  "ga.fecha": "Date",
  "ga.concepto": "Description",
  "ga.categoria": "Category",
  "ga.pago": "Payment",
  "ga.puesto": "Till point",
  "ga.importe": "Amount",
  "ga.compraNumero": " · Purchase #{numero}",
  "ga.anulado": "Void",
  "ga.anuladoPor": "Void: {motivo}",
  "ga.editar": "Edit",
  "ga.anular": "Void",
  "ga.d.editar": "Edit expense",
  "ga.d.nuevo": "New expense",
  "ga.d.fallo": "Couldn't save the expense",
  "ga.d.concepto": "Description",
  "ga.d.conceptoEjemplo": "Electricity bill",
  "ga.d.importe": "Amount (credits)",
  "ga.d.fecha": "Date",
  "ga.d.categoria": "Category",
  "ga.d.formaDePago": "Payment method",
  "ga.d.saldraDeCaja": "It'll come out of the open till.",
  "ga.d.proveedor": "Supplier",
  "ga.d.notas": "Notes",
  "ga.d.editarNota":
    "Editing an expense doesn't change the till movement recorded at the time: that shift's cash count is already closed.",
  "ga.an.titulo": "Void expense #{numero}",
  "ga.an.fallo": "Couldn't void it",
  "ga.an.motivo": "Reason",
  "ga.an.motivoAyuda": "It stays next to the expense, so it makes sense when you go over the month.",
  "ga.an.motivoEjemplo": "The amount was wrong",
  "ga.an.noSeBorra": "It isn't deleted: it stays crossed out in the list.",
  "ga.an.vuelveALaCaja": " The money goes back into the open till.",
  "ga.an.compraNoPagada": " Purchase #{numero} will show as unpaid again.",
  "ga.c.titulo": "Expense categories",
  "ga.c.listo": "Done",
  "ga.c.fallo": "Couldn't do it",
  "ga.c.ejemplo": "Water, Wages, Accountant...",
  "ga.c.anadir": "Add",
  "ga.c.unGasto": "1 expense",
  "ga.c.gastos": "{n} expenses",
  "ga.c.apagadaNota": "Off · not offered when you record",
  "ga.c.seApagara": "It'll be turned off: it has expenses in it",
  "ga.c.quitarla": "Remove it",
  "ga.c.quitar": "Remove",
  "ga.c.volverAUsar": "Use it again",
  "ga.c.nota":
    "A category with expenses in it gets turned off instead of deleted: that way entries from earlier years still say what they were for.",

  "co.titulo": "Expenses and purchases",
  "co.descripcion": "What you order from suppliers, what arrives and what's owed",
  "co.nuevaCompra": "+ Purchase",
  "co.nuevoProveedor": "+ Supplier",
  "co.antesDeLaPrimera":
    "Before the first purchase you need to add the supplier, so they're more than just a name and you can see what they've brought in and what you owe them.",
  "co.pendienteDePagar": "Still to pay",
  "co.aTodos": "Across all suppliers",
  "co.pedidasSinRecibir": "Placed, not received",
  "co.enCamino": "Placed and still on the way",
  "co.sinProveedores": "No suppliers yet.",
  "co.proveedor": "Supplier",
  "co.contacto": "Contact",
  "co.compras": "Purchases",
  "co.seLeDebe": "Owed",
  "co.sinCompras":
    "No purchases yet. When you receive one, its product goes into storage on its own with its cost and its supplier, without typing it in again in Inventory.",
  "co.pedida": "Placed",
  "co.recibida": "Received",
  "co.anulada": "Void",
  "co.debe": "{importe} owed",
  "co.pagada": "Paid",
  "co.recibidaEl": " · received on {fecha}",
  "co.noEsGenero": " · not stock",
  "co.haLlegado": "It's arrived",
  "co.anular": "Void",
  "co.an.titulo": "Void purchase #{numero}",
  "co.an.quePasa":
    "The purchase is voided and stops counting towards what you owe the supplier. Stock is untouched: it hadn't arrived yet.",
  "co.an.motivo": "Reason (optional)",
  "co.an.confirmar": "Void the purchase",
  "co.an.fallo": "Couldn't void the purchase",
  "co.an.hecho": "Purchase #{numero} voided",
  "co.pagar": "Pay",
  "co.recibidaConLotes": "Purchase #{numero} received. {lotes} in storage.",
  "co.recibidaSinLotes": "Purchase #{numero} received.",
  "co.unLote": "1 batch",
  "co.lotes": "{n} batches",
  "co.falloRecibir": "Couldn't receive it",
  "co.p.titulo": "New supplier",
  "co.p.fallo": "Couldn't save",
  "co.p.nombre": "Name",
  "co.p.cif": "CIF or NIF",
  "co.p.telefono": "Phone",
  "co.p.email": "Email",
  "co.p.notas": "Notes",
  "co.n.titulo": "New purchase",
  "co.n.fallo": "Couldn't save the purchase",
  "co.n.registrar": "Record · {importe}",
  "co.n.proveedor": "Supplier",
  "co.n.referencia": "Delivery note or invoice",
  "co.n.referenciaAyuda": "Optional, the number on the paperwork.",
  "co.n.queSeCompra": "What you're buying",
  "co.n.explicacion":
    "Lines linked to a product in the catalogue create their batch when the purchase comes in. The rest — jars, labels, a set of scales — are an expense and don't touch stock.",
  "co.n.producto": "Product",
  "co.n.noEsGenero": "Not stock",
  "co.n.descripcion": "Description",
  "co.n.gramos": "Grams",
  "co.n.coste": "Cost (credits)",
  "co.n.otraLinea": "+ Another line",
  "co.n.quitarUltima": "Remove the last one",
  "co.g.titulo": "Pay purchase #{numero}",
  "co.g.fallo": "Couldn't record the payment",
  "co.g.registrar": "Record the payment",
  "co.g.quedanPorPagar": "{importe} of this purchase is still to pay to {proveedor}.",
  "co.g.importe": "Amount (credits)",
  "co.g.aPlazos": "You can pay in instalments.",
  "co.g.formaDePago": "Payment method",
  "co.g.comoGasto":
    "The payment goes down as an expense tied to this purchase, so the money only leaves once in the books.",
  "co.g.enEfectivo": "In cash, it comes out of the shift's till.",

  "do.titulo": "Donations and top-ups",
  "do.descripcion": "What people give the club and what they put in their wallet to spend inside",
  "do.a": "to",
  "do.exportar": "Export CSV",
  "do.falloExportar": "Couldn't export",
  "do.nueva": "+ Donation",
  "do.tabDonaciones": "Donations",
  "do.tabCartera": "Wallet top-ups",
  "do.deSuCartera": "From their wallet",
  "do.donadoEnPeriodo": "Donated in the period",
  "do.unaDonacion": "1 donation",
  "do.donaciones": "{n} donations",
  "do.sinNombre": "No name",
  "do.delBote": "From the pot, no member behind it",
  "do.enEfectivo": "In cash",
  "do.entraEnCaja": "Goes into the shift's till",
  "do.ingresadoEnPeriodo": "Topped up in the period",
  "do.unIngreso": "1 top-up",
  "do.ingresos": "{n} top-ups",
  "do.clubDebe": "The club now owes",
  "do.saldoVivo": "Outstanding balance across all wallets",
  "do.buscar": "Search by member or description",
  "do.cualquierForma": "Any payment method",
  "do.cualquierPuesto": "Any till point",
  "do.verAnuladas": "Show the voided ones",
  "do.sinDonaciones":
    "No donation matches these filters. They're for recording money someone gives the club that isn't their fee or what they take away.",
  "do.numero": "#",
  "do.fecha": "Date",
  "do.quien": "Who",
  "do.concepto": "Description",
  "do.forma": "Method",
  "do.puesto": "Till point",
  "do.atendio": "Served by",
  "do.importe": "Amount",
  "do.anonima": "Anonymous",
  "do.anulada": "Void",
  "do.anular": "Void",
  "do.sinIngresos":
    "Nobody has put money in their wallet with these filters. Top-ups are done from the member's record or at the counter.",
  "do.socio": "Member",
  "do.loCobro": "Charged by",
  "do.ingresado": "Topped up",
  "do.saldoDespues": "Balance after",
  "do.sinDato": "No data",
  "do.r.titulo": "Record a donation",
  "do.r.fallo": "Couldn't record the donation",
  "do.r.registrar": "Record",
  "do.r.quienDona": "Who's donating",
  "do.r.quienDonaAyuda": "You can leave it blank: some people drop money in the pot without giving their name.",
  "do.r.quitar": "Remove",
  "do.r.buscarSocio": "Search for a member, or leave it blank",
  "do.r.importe": "Amount (credits)",
  "do.r.formaDePago": "Payment method",
  "do.r.faltaSocio": "To donate from a wallet you have to say which member.",
  "do.r.efectivoNota": "It goes into the shift's till. The other methods don't touch it.",
  "do.r.concepto": "Description",
  "do.r.conceptoAyuda": "Optional. For example: bar pot, raffle, extra contribution.",
  "do.an.titulo": "Void the donation",
  "do.an.fallo": "Couldn't void it",
  "do.an.anulando": "Voiding...",
  "do.an.anular": "Void",
  "do.an.explicacion":
    "You're voiding {importe}. The donation isn't deleted: it's marked as voided and stops counting in the books. Money coming in that vanishes from the history is a hole in the cash count.",
  "do.an.efectivo": "The money comes out of the open till with its own entry.",
  "do.an.cartera": "The balance goes back to the member.",
  "do.an.motivo": "Reason",
  "do.an.motivoAyuda": "Optional, but it helps make sense of it six months from now.",

  "semana.1c": "M",
  "semana.2c": "T",
  "semana.3c": "W",
  "semana.4c": "T",
  "semana.5c": "F",
  "semana.6c": "S",
  "semana.0c": "S",
  "semana.1": "Monday",
  "semana.2": "Tuesday",
  "semana.3": "Wednesday",
  "semana.4": "Thursday",
  "semana.5": "Friday",
  "semana.6": "Saturday",
  "semana.0": "Sunday",

  "de.titulo": "Discounts",
  "de.descripcion": "Campaigns by category, product, membership type, day and time slot",
  "de.nuevo": "+ Discount",
  "de.unoSolo": "one discount only, the biggest",
  "de.noSeAcumulan": "that fits: they don't stack with each other, or with the membership type's overall discount.",
  "de.enCadaEntrega": "Every collection gets",
  "de.sinDescuentos": "No discounts yet. Create one for, say, a flower afternoon at 15% off.",
  "de.nombre": "Name",
  "de.descuento": "Discount",
  "de.aplicaA": "Applies to",
  "de.cuando": "When",
  "de.estado": "Status",
  "de.categoriaNombre": "{nombre} category",
  "de.todoElCatalogo": "The whole catalogue",
  "de.soloTipo": "{tipo} only",
  "de.todosLosDias": "Every day",
  "de.activo": "Active",
  "de.apagado": "Off",
  "de.editar": "Edit",
  "de.borrar": "Delete",
  "de.falloBorrar": "Couldn't delete it",
  "de.d.editar": "Edit {nombre}",
  "de.d.nuevo": "New discount",
  "de.d.fallo": "Couldn't save the discount",
  "de.d.nombre": "Name",
  "de.d.nombreAyuda": "Shows on the receipt, e.g. “Quiet afternoon”.",
  "de.d.porcentaje": "Discount (%)",
  "de.d.categoria": "Category",
  "de.d.categoriaAyuda": "Empty = all of them.",
  "de.d.todas": "All",
  "de.d.producto": "One product only",
  "de.d.productoAyuda": "Empty = everything in the category.",
  "de.d.todos": "All",
  "de.d.tipoSocio": "One membership type only",
  "de.d.tipoSocioAyuda": "Empty = everyone.",
  "de.d.todosLosSocios": "All members",
  "de.d.dias": "Days of the week",
  "de.d.diasAyuda": "None ticked = every day.",
  "de.d.desdeLas": "From",
  "de.d.franjaAyuda": "Time slot; empty = all day.",
  "de.d.hastaLas": "Until",
  "de.d.fechaInicio": "Start date",
  "de.d.fechaAyuda": "Empty = no limit.",
  "de.d.fechaFin": "End date",
  "de.d.activo": "Active",

  "ca.titulo": "Menus",
  "ca.descripcion": "What members see on the screen in the club, or when they scan a QR code",
  "ca.dispensario": "Dispensary",
  "ca.barra": "Bar",
  "ca.sinCartaBarra": "No bar menu. If you sell drinks or food, this is where you show the list.",
  "ca.sinCartaDispensario": "No dispensary menu.",
  "ca.principalAyuda": "The one that shows on the screen in the club",
  "ca.principal": "★ Main",
  "ca.apagada": "Off",
  "ca.creadaEl": "Created on {fecha}",
  "ca.nCategorias": " · {n} categories",
  "ca.verQr": "See the QR code and the link",
  "ca.qr": "QR",
  "ca.editar": "Edit",
  "ca.nuevaDe": "+ {zona} menu",
  "ca.laPrincipal": "main",
  "ca.notaPrincipal1": "The",
  "ca.notaPrincipal2":
    "menu for each area is the one that shows on the screen in the club. The rest open with their own link, so you can put a QR code on the tables or send one to someone.",
  "ca.fallo": "It didn't work",
  "ca.n.titulo": "New menu",
  "ca.n.nombreBarra": "Bar menu",
  "ca.n.nombreDispensario": "Dispensary menu",
  "ca.n.crear": "Create",
  "ca.n.fallo": "Couldn't create it",
  "ca.n.nombre": "Name",
  "ca.n.nombreAyuda": "Only you see it, to tell them apart.",
  "ca.n.avisoBarra": "It'll show whatever you've set up on the bar.",
  "ca.n.avisoDispensario":
    "It'll show the products you have in stock. You can narrow it to a few categories afterwards.",
  "ca.e.nombre": "Name",
  "ca.e.mensaje": "Message",
  "ca.e.mensajeAyuda": "Shows right at the top. Opening hours, say, or something new.",
  "ca.e.categorias": "Categories it shows",
  "ca.e.categoriasAyuda": "None ticked: it shows them all.",
  "ca.e.encendida": "On",
  "ca.e.encendidaAyuda": "Turn it off and its link stops opening. Nothing gets deleted.",
  "ca.e.ponerlaEnPantalla": "Put it on the screen in the club",
  "ca.e.hacerlaPrincipal": "★ Make it the main one",
  "ca.e.yaNoLaUsais": "You don't use it any more",
  "ca.e.confirmarBorrar": "Delete the “{nombre}” menu? Its link will stop opening.",
  "ca.e.borrar": "Delete",
  "ca.e.falloBorrar": "Couldn't delete it",
  "ca.e.falloGuardar": "Couldn't save it",
  "ca.q.titulo": "QR code for {nombre}",
  "ca.q.apagada": "This menu is off: the link won't open until you turn it on.",
  "ca.q.enlace": "Link",
  "ca.q.siSeHaFiltrado": "If the link has leaked",
  "ca.q.confirmarRenovar": "A new link gets created and the current one stops opening. Carry on?",
  "ca.q.cambiarEnlace": "Change the link",
  "ca.q.soloEsta": "Only this menu's link changes. The rest keep working.",

  "pd.volver": "Back",
  "pd.entradasYMermas": "Stock in and waste",
  "pd.stock": "Stock",
  "pd.minimo": "Minimum {cantidad}",
  "pd.sinMinimo": "No minimum set",
  "pd.aportacion": "Contribution",
  "pd.costeMedio": "Average cost",
  "pd.sinCoste": "The batches have no cost",
  "pd.segunLotes": "Based on the batches that have a cost",
  "pd.thcCbd": "THC / CBD",
  "pd.disponible": "Available at the counter",
  "pd.retirado": "Pulled from the counter",
  "pd.lotes": "Batches",
  "pd.sinLotes": "This product has no batches: the stock went in as an adjustment, with no source.",
  "pd.lote": "Batch",
  "pd.entrada": "Received",
  "pd.origen": "Source",
  "pd.inicial": "Initial",
  "pd.queda": "Left",
  "pd.coste": "Cost",
  "pd.caducidad": "Expiry",
  "pd.estado": "Status",
  "pd.consumido": "{n}% used",
  "pd.agotado": "Used up",
  "pd.caducado": "Expired",
  "pd.enUso": "In use",
  "pd.ultimosMovimientos": "Latest movements",
  "pd.sinMovimientos": "No movements.",
  "pd.fecha": "Date",
  "pd.tipo": "Type",
  "pd.detalle": "Detail",
  "pd.cantidad": "Amount",
  "pd.stockDespues": "Stock after",
  "pd.usuario": "User",

  "re.titulo": "Stock counts",
  "re.descripcion": "Weigh what's there and compare it with what should be there",
  "re.a": "to",
  "re.empezar": "+ Start a count",
  "re.falloEmpezar": "Couldn't start the stock count",
  "re.cuadra": "Balanced",
  "re.enMarcha": "Stock count #{numero} under way",
  "re.empezado": "Started {cuando} by {quien}. {contados} of {total} counted.",
  "re.alguien": "someone",
  "re.conLocalCerrado":
    "Count with the club closed if you can. Anything dispensed meanwhile isn't lost, but the discrepancy comes out cleaner.",
  "re.seguirContando": "Carry on counting",
  "re.delPeriodo": "Stock counts in this period",
  "re.cerrados": "Closed",
  "re.todaviaNinguno": "None yet",
  "re.descuadreAcumulado": "Total discrepancy",
  "re.sumandoTodos": "Adding up every count in the period",
  "re.elPeor": "The worst",
  "re.elDiaQueMas": "The day it drifted furthest",
  "re.sinCerrados":
    "No closed stock counts in this period. A stock count is weighing everything there at closing time and seeing if it matches what the system says.",
  "re.numero": "No.",
  "re.cuando": "When",
  "re.quien": "Who",
  "re.contados": "Counted",
  "re.saltados": "Skipped",
  "re.descuadre": "Discrepancy",
  "re.seCorrigio": "Corrected",
  "re.tardo": "Took",
  "re.si": "Yes",
  "re.no": "No",
  "re.unMinuto": "1 minute",
  "re.minutos": "{n} minutes",
  "re.ver": "View",
  "re.d.titulo": "Stock count #{numero}",
  "re.d.recuento": "Stock count",
  "re.d.seguirLuego": "Finish later",
  "re.d.cerrarRecuento": "Close the count",
  "re.d.contadosDe": "{contados} of {total} counted",
  "re.d.nSaltados": " · {n} skipped",
  "re.d.saltarLoQueQueda": "Skip what's left ({n})",
  "re.d.descuadre": "Discrepancy:",
  "re.d.producto": "Product",
  "re.d.deberiaHaber": "Should be",
  "re.d.hay": "Actual",
  "re.d.saltarElResto": "Skip the rest",
  "re.d.saltado": "Skipped",
  "re.d.volverAContar": "Count it again",
  "re.d.saltarEste": "Skip this product",
  "re.d.contar": "Count",
  "re.d.saltar": "Skip",
  "re.d.notas": "Notes:",
  "re.d.seCorrigio": "Closing it set the stock to what was counted.",
  "re.d.noSeCorrigio": "Closed without correcting the stock: the discrepancy was noted down, nothing else.",
  "re.d.falloSaltar": "Couldn't skip it",
  "re.d.falloApuntar": "Couldn't note it down",
  "re.c.titulo": "Close the stock count",
  "re.c.volver": "Back",
  "re.c.cerrando": "Closing...",
  "re.c.cerrar": "Close",
  "re.c.fallo": "Couldn't close it",
  "re.c.hasContadoUno": "You've counted 1 product and the discrepancy is",
  "re.c.hasContado": "You've counted {n} products and the discrepancy is",
  "re.c.corregir": "Correct the stock to what you counted",
  "re.c.corregirAyuda":
    "The difference gets added to what's there now, so if anything was dispensed while you counted, that collection still counts. Every correction leaves its own entry in Inventory.",
  "re.c.notas": "Notes",
  "re.c.notasAyuda": "For example: why it's missing, or what you didn't get to.",
  "re.c.noSeBorra": "A closed stock count can't be deleted: it's the paperwork that explains that day's discrepancy.",

  "ct.titulo": "Catalogue",
  "ct.descripcion": "Strains and products the club dispenses",
  "ct.importar": "Import CSV",
  "ct.marcas": "Brands",
  "ct.nuevaCategoria": "+ Category",
  "ct.nuevoProducto": "+ Product",
  "ct.sinProductos":
    "No products in the catalogue yet. The categories are already set up: all that's left is adding what you dispense, with its contribution.",
  "ct.producto": "Product",
  "ct.categoria": "Category",
  "ct.genetica": "Genetics",
  "ct.thcCbd": "THC / CBD",
  "ct.precio": "Contribution",
  "ct.stock": "Stock",
  "ct.estado": "Status",
  "ct.bajoMinimo": "below minimum",
  "ct.activo": "Active",
  "ct.inactivo": "Inactive",
  "ct.editar": "Edit",
  "ct.p.editar": "Edit {nombre}",
  "ct.p.nuevo": "New product",
  "ct.p.fallo": "Couldn't save the product",
  "ct.p.nombre": "Name",
  "ct.p.categoria": "Category",
  "ct.p.marca": "Brand",
  "ct.p.sinMarcasAun": "You haven't created any yet.",
  "ct.p.sinMarca": "No brand",
  "ct.p.genetica": "Genetics",
  "ct.p.sinEspecificar": "Not specified",
  "ct.p.thc": "THC (%)",
  "ct.p.cbd": "CBD (%)",
  "ct.p.aportacionUnidad": "Contribution per unit (credits)",
  "ct.p.aportacionGramo": "Contribution per gram (credits)",
  "ct.p.stockMinimo": "Minimum stock ({unidad})",
  "ct.p.stockMinimoAyuda": "You get an alert when it drops below this.",
  "ct.p.precioPorTipo": "Fixed contribution by membership type",
  "ct.p.sustituye": "It replaces the catalogue contribution; the type's discount isn't applied on top.",
  "ct.p.vacioConDescuento": "Empty: catalogue contribution with −{porcentaje}%",
  "ct.p.vacioCatalogo": "Empty: catalogue contribution",
  "ct.p.descripcion": "Description",
  "ct.p.disponible": "Available at the counter",
  "ct.c.titulo": "New category",
  "ct.c.fallo": "Couldn't create the category",
  "ct.c.crear": "Create",
  "ct.c.nombre": "Name",
  "ct.c.color": "Colour",
  "ct.c.seDispensaPor": "Dispensed by",
  "ct.c.peso": "Weight (grams)",
  "ct.c.unidades": "Units",
  "ct.m.titulo": "Brands",
  "ct.m.listo": "Done",
  "ct.m.fallo": "Couldn't do it",
  "ct.m.ejemplo": "Cookies, Green House, home-grown...",
  "ct.m.anadir": "Add",
  "ct.m.sinMarcas": "No brands. They tell you whose each product is; if you grow your own, you don't need them.",
  "ct.m.unProducto": "1 product",
  "ct.m.productos": "{n} products",
  "ct.m.apagada": "Off",
  "ct.m.apagar": "Turn off",
  "ct.m.encender": "Turn on",
  "ct.m.confirmarConProductos": "{productos} will be left with no brand. Nothing else is deleted. Carry on?",
  "ct.m.confirmarBorrar": "Delete the brand “{nombre}”?",
  "ct.m.borrar": "Delete",
  "ct.m.nota":
    "Turning it off takes it out of the drop-down but leaves the products that already have it. Deleting it just leaves them with no brand, nothing more.",

  "ci2.titulo": "Import catalogue",
  "ci2.descripcion": "Bring in the products and stock you've got from another program or from a spreadsheet",
  "ci2.volver": "Back",
  "ci2.importadoUno": "1 product imported. It's in the catalogue and at the counter now.",
  "ci2.importadoUnoConStock":
    "1 product imported with its stock as an incoming batch. It's in the catalogue and at the counter now.",
  "ci2.importados": "{n} products imported. They're in the catalogue and at the counter now.",
  "ci2.importadosConStock":
    "{n} products imported, {conStock} with their stock as an incoming batch. They're in the catalogue and at the counter now.",
  "ci2.verCatalogo": "View the catalogue",
  "ci2.verInventario": "View the inventory",
  "ci2.otroFichero": "Import another file",
  "ci2.paso1": "1. Get the file ready",
  "ci2.explicacion":
    "A CSV with one row per product. You need at least «Product», «Category» and «Price» (per gram, or per unit in the categories that go by units). It also understands the names other programs use, and the ones in Spanish, Catalan, Dutch and German: «Item Name», «Donation per gram», «Unit Price», «Precio», «Prijs» or «Preis» all work. If there's «Stock», it comes in as a batch with its «Cost price», «Supplier», «Batch» and «Best before» if you put them in. The rest (THC, CBD, genetics, minimum stock, brand, description, active) is used if it's there.",
  "ci2.formatos":
    "The categories have to exist in Catalogue already, with their unit: that way a typo doesn't create a new category. Numbers with a comma or a full stop (“8,50” or “8.5”); dates 2027-03-01 or 01/03/2027; separator “;” or “,”. Files saved by Excel are fine.",
  "ci2.descargarPlantilla": "Download template",
  "ci2.falloPlantilla": "Couldn't download the template",
  "ci2.paso2": "2. Pick the file and check it",
  "ci2.analizarNoGuarda": "Check saves nothing: it just looks the file over and tells you what it finds.",
  "ci2.analizando": "Checking...",
  "ci2.analizar": "Check",
  "ci2.paso3": "3. Result",
  "ci2.procesando": "Processing the file...",
  "ci2.eligeFichero": "Pick a file and press Check.",
  "ci2.filas": "Rows",
  "ci2.validas": "Valid",
  "ci2.conErrores": "With errors",
  "ci2.columnas": "Columns recognised: {columnas}",
  "ci2.corrige": "Fix these rows in the file and check it again. Nothing is imported while there are errors.",
  "ci2.fila": "Row",
  "ci2.problema": "Problem",
  "ci2.producto": "Product",
  "ci2.categoria": "Category",
  "ci2.precio": "Contribution",
  "ci2.stock": "Stock",
  "ci2.seMuestran": "Showing {cuantos} of {total}.",
  "ci2.importarUno": "Import 1 product",
  "ci2.importarVarios": "Import {n} products",
  "ci2.conStock": " ({n} with stock)",
  "ci2.falloLeer": "Couldn't read the file",
  "ci2.falloImportar": "Couldn't import",

  "di.titulo": "Opening days",
  "di.descripcion": "Which days the club opened and how each one went",
  "di.a": "to",
  "di.unDiaSinCerrar": "1 day with the till still open.",
  "di.diasSinCerrar": "{n} days with the till still open.",
  "di.arqueoNoCuadra": "The cash count for those days won't add up until they're closed.",
  "di.irACaja": "Go to Till",
  "di.diasAbiertos": "Days open",
  "di.deTantos": "{abiertos} of {total}",
  "di.delPeriodo": "In the period you picked",
  "di.entradoEnTotal": "Taken in total",
  "di.entregasBarraCuotas": "Collections, bar and fees",
  "di.sociosDistintos": "Different members",
  "di.hanPasado": "Have been through the club",
  "di.soloAbiertos": "Only the days we opened",
  "di.cuandoCuentaAbierto":
    "A day counts as open as soon as someone checks in, something is collected, the bar takes money or a till is opened.",
  "di.noAbrioNinguno": "The club didn't open a single day in this period.",
  "di.sinDias": "There are no days in the period you picked.",
  "di.dia": "Day",
  "di.socios": "Members",
  "di.entregas": "Collections",
  "di.dispensado": "Dispensed",
  "di.barra": "Bar",
  "di.cuotas": "Fees",
  "di.total": "Total",
  "di.caja": "Till",
  "di.cerrado": "Closed",
  "di.unAlta": "1 new member",
  "di.altas": "{n} new members",
  "di.sinCaja": "No till",
  "di.sinCerrar": "Still open",
  "di.cuadro": "Balanced",
  "di.notaTotal":
    "The total adds up collections, bar and fees. It leaves out wallet top-ups, which aren't the club's money, and it doesn't take off expenses.",
  "di.diaYNumero": "{dia} {numero}",

  "ac.titulo": "Door log",
  "ac.descripcion": "A record of members coming in and out of the premises",
  "ac.fallo": "Couldn't log the check-in",
  "ac.registrarEntrada": "Check in",
  "ac.identificar": "Find member...",
  "ac.alElegir":
    "Pick a member and their check-in is logged. If their membership has expired or they're suspended, you'll get an alert and they can't come in.",
  "ac.socioNumero": "Member #{numero}",
  "ac.accesoRegistrado": "Check-in logged",
  "ac.cartera": "Wallet: {importe}",
  "ac.cuotaValida": "Member until {fecha}",
  "ac.irAlMostrador": "Go to the counter",
  "ac.dentroAhora": "Inside now",
  "ac.accesosHoy": "Check-ins today",
  "ac.enElLocal": "Members on the premises",
  "ac.nadieDentro": "Nobody is logged as being on the premises.",
  "ac.desde": "since {hora}",
  "ac.deHoy": "Today's check-ins",
  "ac.ningunoHoy": "No check-ins logged yet today.",
  "ac.hora": "Time",
  "ac.socio": "Member",
  "ac.tipo": "Type",
  "ac.entrada": "In",
  "ac.salida": "Out",

  "co2.titulo": "Membership agreement",
  "co2.descripcion": "The sworn declaration and the rules that anyone joining signs",
  "co2.fallo": "Couldn't save the agreement",
  "co2.sinCambios": "You haven't changed anything: it's still version {numero}.",
  "co2.guardadoConRaros":
    "Saved as version {numero}. Careful: {huecos} can't be filled in and will come out as-is in the document.",
  "co2.guardado": "Saved as version {numero}.",
  "co2.sinEscribir":
    "You haven't written the agreement yet. Until you do, there'll be nothing to sign when people join and members won't see it in their portal.",
  "co2.versionActual": "Current version",
  "co2.numeroVersion": "No. {numero}",
  "co2.publicada": "Published {fecha}",
  "co2.sinEscribirPie": "Not written",
  "co2.loHanFirmado": "Have signed it",
  "co2.contandoVersiones": "Members, counting every version",
  "co2.sinFirmar": "Not signed",
  "co2.faltanDeAlta": "Active members still to sign",
  "co2.ningunoPendiente": "None pending",
  "co2.elTexto": "The text",
  "co2.elTextoDeHoy": "Today's text",
  "co2.ejemplo": "Sworn declaration\n\nI, {{NOMBRE}}, holder of {{DNI}}, declare...",
  "co2.seGuardaTalCual":
    "It's saved exactly as you write it, line breaks and all. Every time you change the text it creates a new version; the old one isn't deleted, because whoever signed it signed that one.",
  "co2.huecos": "Blanks you can use",
  "co2.huecosAyuda": "Write them in double curly brackets and they're filled in with each member's details.",
  "co2.versiones": "Versions",
  "co2.sinVersiones": "There aren't any yet.",
  "co2.numero": "No.",
  "co2.publicadaColumna": "Published",
  "co2.firmas": "Signatures",
  "co2.ver": "View",
  "co2.sinFirmarCuantos": "Not signed ({n})",
  "co2.socioColumna": "Member",
  "co2.deAltaDesde": "Joined",
  "co2.unSocioDeAntes": "1 member from before you wrote the agreement.",
  "co2.sociosDeAntes": "{n} members from before you wrote the agreement.",
  "co2.seFirmaDesde": "They sign it from their record, or from the portal if they've got one open.",
  "co2.v.titulo": "Agreement · version {numero}",
  "co2.v.publicadaEl": "Published on {fecha}.",

  "in.titulo": "Reports",
  "in.descripcion": "The association's activity in the period you picked",
  "in.a": "to",
  "in.porDia": "By day",
  "in.porMes": "By month",
  "in.aportaciones": "Contributions",
  "in.unaEntrega": "1 collection",
  "in.entregas": "{n} collections",
  "in.dispensado": "Dispensed",
  "in.cuotasCobradas": "Fees taken",
  "in.unaCuota": "1 fee",
  "in.cuotas": "{n} fees",
  "in.gastos": "Expenses",
  "in.verDesglose": "See the breakdown",
  "in.evolucion": "Dispensing over time",
  "in.sinActividad": "No activity recorded in this period.",
  "in.unPeriodo": "1 period",
  "in.periodos": "{n} periods",
  "in.masDispensados": "Most dispensed products",
  "in.sinDatos": "No data in the period.",
  "in.producto": "Product",
  "in.cantidad": "Amount",
  "in.masActivos": "Most active members",
  "in.socio": "Member",
  "in.visitas": "Visits",
  "in.queMasAportan": "Who contributes most",
  "in.nadieHaAportado": "Nobody has contributed anything in the period.",
  "in.enGenero": "{importe} on product",
  "in.enBarra": "{importe} at the bar",
  "in.deCuota": "{importe} in fees",
  "in.donado": "{importe} donated",
  "in.noCuentaCartera":
    "This leaves out what they've put in their wallet: that money is still theirs until they spend it.",
  "in.porFormaDePago": "Contributions by payment method",
  "in.recargasCartera": "Wallet top-ups",
  "in.queOpinan": "What members think",
  "in.nota": "Rating",
  "in.votos": "Votes",
  "in.queDicen": "What they say",
  "in.nadieHaEscrito": "Nobody has written anything yet. Ratings with no comment still count towards the average.",

  "cm.titulo": "Community",
  "cm.descripcion": "Applications to join and news for the member portal",
  "cm.nuevaNoticia": "+ News item",
  "cm.tabSolicitudes": "Applications",
  "cm.tabNoticias": "News",
  "cm.pendientes": "Pending",
  "cm.resueltas": "Decided",
  "cm.enlaceAlta": "Link to apply to join",
  "cm.copiarEnlace": "Copy link",
  "cm.solicitudesPendientes": "Pending applications",
  "cm.sinPendientes": "There are no pending applications.",
  "cm.recibida": "Received",
  "cm.persona": "Person",
  "cm.contacto": "Contact",
  "cm.avalista": "Sponsor",
  "cm.nacidoEl": "{documento} · born {fecha}",
  "cm.socioNumero": "Member no. {numero}",
  "cm.aprobar": "Approve",
  "cm.rechazar": "Reject",
  "cm.aprobada": "{persona} is now member no. {numero}, waiting to pay the fee.",
  "cm.falloAprobar": "Couldn't approve it",
  "cm.estado": "Status",
  "cm.resultado": "Result",
  "cm.etiquetaAprobada": "Approved",
  "cm.etiquetaRechazada": "Rejected",
  "cm.sinNoticias": "No news. Publish one and members will see it in their portal.",
  "cm.fecha": "Date",
  "cm.tituloColumna": "Title",
  "cm.publicadoPor": "Published by",
  "cm.publicada": "Published",
  "cm.borrador": "Draft",
  "cm.editar": "Edit",
  "cm.borrar": "Delete",
  "cm.confirmarBorrar": "Delete “{titulo}”?",
  "cm.n.editar": "Edit news item",
  "cm.n.nueva": "New news item",
  "cm.n.fallo": "Couldn't save the news item",
  "cm.n.titulo": "Title",
  "cm.n.texto": "Text",
  "cm.n.textoAyuda": "Members will see this in their portal.",
  "cm.n.publicada": "Published (untick it and it stays a draft)",
  "cm.n.avisar": "Alert members on their phones",
  "cm.n.avisarAyuda": "It only reaches the ones who've turned alerts on in their portal.",
  "cm.n.fotos": "Photos",
  "cm.n.quitar": "Remove",
  "cm.n.subiendo": "Uploading…",
  "cm.n.otraFoto": "Add another photo",
  "cm.n.anadirFotos": "Add photos",
  "cm.n.escribePrimero": "Write the title and the text first: the post is saved when you add the first photo.",
  "cm.n.hastaOcho": "Up to 8 photos. JPG, PNG or WEBP.",
  "cm.n.falloSubir": "Couldn't upload the photo",
  "cm.n.falloQuitar": "Couldn't remove the photo",
  "cm.r.titulo": "Reject {nombre}'s application",
  "cm.r.rechazar": "Reject",
  "cm.r.fallo": "Couldn't reject it",
  "cm.r.queda": "The application stays on file. The reason is for your records only: the person isn't told.",
  "cm.r.motivo": "Reason (optional)",

  "aj.titulo": "Settings",
  "aj.descripcion": "Association details, limits and team",
  "aj.guardarCambios": "Save changes",
  "aj.guardados": "Settings saved.",
  "aj.fallo": "Couldn't save the settings",
  "aj.datos": "Association details",
  "aj.nombre": "Name",
  "aj.cif": "CIF",
  "aj.telefono": "Phone",
  "aj.direccion": "Address",
  "aj.ciudad": "Town",
  "aj.provincia": "Province",
  "aj.cp": "Postcode",
  "aj.email": "Contact email",
  "aj.reglas": "Dispensing rules",
  "aj.limiteDiario": "Daily limit (g)",
  "aj.limiteDiarioAyuda": "Per member, per day.",
  "aj.limiteMensual": "Monthly limit (g)",
  "aj.limiteMensualAyuda": "Per member, per calendar month.",
  "aj.cuotaAnual": "Annual fee (credits)",
  "aj.limitesPropios": "Any member can have their own limits that override these.",
  "aj.aforo": "Venue capacity",
  "aj.aforoAyuda":
    "The most people allowed in, as per your activity licence. It shows at the counter next to how many are inside. Leave it at 0 if you'd rather not track it.",
  "aj.aforoNota":
    "When you hit capacity you get an alert, but nobody is stopped at the door: the count depends on people checking out, and turning a member away over a number that may be off would be worse than not having it.",
  "aj.ticket": "Collection receipt",
  "aj.idiomaPortal": "Member portal language",
  "aj.idiomaPortalAyuda":
    "The language members see the portal in, and the one the fee emails come in. Each member can change it on their phone.",
  "aj.anchoPapel": "Paper width",
  "aj.anchoPapelAyuda": "For the thermal printer at the counter.",
  "aj.ancho80": "80 mm (standard)",
  "aj.ancho58": "58 mm (narrow)",
  "aj.textoTicket": "Receipt text",
  "aj.textoTicketAyuda": "Prints at the foot of every collection.",
  "aj.avisoLegal": "Legal notice",
  "aj.avisoCuota": "Automatic fee reminder",
  "aj.avisoCuotaNota":
    "Once a day, members whose membership is about to expire get a reminder by email (and on their phone, if they've installed the portal). Each member gets one reminder per fee: when they renew, they come round again the next year.",
  "aj.avisarAuto": "Send reminders automatically",
  "aj.cuantosDiasAntes": "How many days before",
  "aj.cuantosDiasAyuda": "Members whose membership has already expired get one too.",
  "aj.avisoApagado":
    "It comes switched off on purpose: writing to your members is the club's call, not ours. You can send them by hand from Renewals whenever you like, even with this left off.",
  "aj.equipo": "Team",
  "aj.nuevoUsuario": "+ User",
  "aj.usuario": "User",
  "aj.rol": "Role",
  "aj.ultimoAcceso": "Last sign-in",
  "aj.desactivado": "Disabled",
  "aj.nunca": "Never",
  "aj.permisosAMedida": "custom permissions",

  "permiso.verSocios": "View the member register",
  "permiso.crearSocio": "Sign up new members",
  "permiso.editarSocio": "Edit a member's record",
  "permiso.registrarAcceso": "Check members in",
  "permiso.dispensar": "Dispense at the counter",
  "permiso.cobrarCuota": "Charge fees",
  "permiso.recargarCartera": "Top up a member's wallet",
  "permiso.verCatalogo": "View the catalogue",
  "permiso.venderBarra": "Take payment at the bar",
  "permiso.gestionarInventario": "Stock in, waste and stock counts",
  "permiso.anularDispensacion": "Void collections",
  "permiso.suspenderSocio": "Suspend members",
  "permiso.gestionarCaja": "Open and close the till",
  "permiso.gestionarGastos": "Record expenses, purchases and donations",
  "permiso.descuentoManual": "Apply manual discounts at the counter",
  "permiso.moverGuardado": "Move stock from storage to the counter",
  "permiso.verInformes": "View reports and export",
  "permiso.ajustarCartera": "Refund or adjust balance",
  "permiso.gestionarPublicaciones": "Publish news",
  "permiso.gestionarSolicitudes": "Decide applications to join",
  "permiso.gestionarCatalogo": "Create and edit products",
  "permiso.gestionarBarra": "Manage the bar menu",
  "permiso.anularCuota": "Void fees already charged",
  "permiso.gestionarUsuarios": "Manage the team",
  "permiso.ajustesClub": "Change the club's settings",
  "permiso.bajaSocio": "End memberships",
  "permiso.importarSocios": "Import members from CSV",
  "permiso.importarProductos": "Import catalogue and stock from CSV",
  "permiso.verAuditoria": "View the activity log",
  "permiso.borrarDatos": "Delete a member's data",
  "permiso.copiaSeguridad": "Download backups",

  "aj.cs.titulo": "Sign everyone out",
  "aj.cs.explicacion":
    "Signs the whole team out of every screen, you included. To sign just one person out, you don't need this: disable their account up above and they're out just the same.",
  "aj.cs.boton": "Sign the whole team out",
  "aj.cs.queVaAPasar":
    "Every open screen will ask for the password again the next time it talks to the server, a few seconds from now. Yours too.",
  "aj.cs.loPendiente":
    "Collections already charged but not sent yet aren't lost: they wait on the tablet and go up on their own as soon as someone signs in again.",
  "aj.cs.confirmar": "Sign everyone out",
  "aj.cs.cerrando": "Signing out…",
  "aj.cs.fallo": "Couldn't sign everyone out",
  "aj.editar": "Edit",
  "aj.tiposDeSocio": "Membership types",
  "aj.nuevoTipo": "+ Type",
  "aj.sinTipos": "No types yet. Create a “Supporter” type, say, with 10% off and a reduced fee.",
  "aj.tipo": "Type",
  "aj.descuento": "Discount",
  "aj.cuota": "Fee",
  "aj.dura": "Lasts",
  "aj.limites": "Limits",
  "aj.socios": "Members",
  "aj.laDelClub": "the club's",
  "aj.losDelClub": "the club's",
  "aj.limitesDiaMes": "{diario} / day · {mensual} / month",
  "aj.registro": "Activity log",
  "aj.sinRegistros": "Nothing logged.",
  "aj.sistema": "system",
  "aj.copia": "Backup",
  "aj.copiaNota":
    "Download the data and keep it encrypted off site. Member photos sit in the server's “uploads” folder and aren't included.",
  "aj.copiaDatos": "Club data (JSON)",
  "aj.copiaBase": "Full database (.db)",
  "aj.falloCopia": "Couldn't download the backup",
  "aj.variosClubes": "This installation holds several clubs: the full server backup is done by whoever administers it.",
  "aj.copiasAuto": "Automatic backups",
  "aj.copiasAutoNota": "A backup is saved every night, and the last 14 days are kept.",
  "aj.sinCopiasAun":
    "There aren't any yet: the first one runs tonight. Even so, save one off the server yourself now and then.",
  "aj.u.titulo": "New team member",
  "aj.u.fallo": "Couldn't create the user",
  "aj.u.crear": "Create",
  "aj.u.nombre": "Name",
  "aj.u.email": "Email",
  "aj.u.contrasena": "Password",
  "aj.u.minimo": "At least 8 characters.",
  "aj.u.rol": "Role",
  "aj.u.trabajadorLargo": "Staff — the day to day: counter, members, inventory and till",
  "aj.e.titulo": "Edit {nombre}",
  "aj.e.fallo": "Couldn't save the user",
  "aj.e.nombre": "Name",
  "aj.e.rol": "Role",
  "aj.e.puesto": "Till point",
  "aj.e.puestoAyuda": "Sets which till this person's takings land in. With no till point, they land in the club till.",
  "aj.e.sinPuesto": "No till point",
  "aj.e.nuevaContrasena": "New password",
  "aj.e.nuevaContrasenaAyuda": "Leave it blank to keep the current one. At least 8 characters.",
  "aj.e.cuentaActiva": "Account active",
  "aj.e.noTuPropia": "You can't disable your own account.",
  "aj.e.desactivada": "A disabled account can't sign in, but keeps its history.",
  "aj.e.permisos": "Permissions",
  "aj.e.permisosAyuda":
    "The role sets what they can do by default. Here you can make exceptions for this person: anything you change is highlighted.",
  "aj.e.concedido": "granted",
  "aj.e.retirado": "removed",
  "aj.e.volverAlRol": "Back to the role's permissions",
  "aj.t.editar": "Edit {nombre}",
  "aj.t.nuevo": "New membership type",
  "aj.t.fallo": "Couldn't save the type",
  "aj.t.falloBorrar": "Couldn't delete the type",
  "aj.t.borrar": "Delete",
  "aj.t.nombre": "Name",
  "aj.t.color": "Colour",
  "aj.t.descripcion": "Description",
  "aj.t.descuento": "General discount (%)",
  "aj.t.descuentoAyuda": "Off the catalogue contribution, unless the product has a fixed contribution for this type.",
  "aj.t.cuota": "Annual fee (credits)",
  "aj.t.cuotaAyuda": "Blank = the club's fee. 0 = no fee.",
  "aj.t.duracion": "How long it lasts (months)",
  "aj.t.duracionAyuda": "12 is the usual one. With 1, the member pays every month.",
  "aj.t.limiteDiario": "Daily limit (g)",
  "aj.t.limiteDiarioAyuda": "Blank = the club's.",
  "aj.t.limiteMensual": "Monthly limit (g)",
  "aj.t.orden": "Sort order",
  "aj.t.activo": "Active (can be given to new members)",
  "aj.p.titulo": "Till points",
  "aj.p.nota":
    "If the club takes money in more than one place at once — the counter and the bar, each with its own drawer — create a till point for each and give each person theirs from Team. Every till point opens and counts its own till.",
  "aj.p.nota2": "If you create none, everything stays as it is now: one single till for the whole club.",
  "aj.p.inactivo": " · inactive",
  "aj.p.unaPersona": "1 person",
  "aj.p.personas": "{n} people",
  "aj.p.desactivar": "Disable",
  "aj.p.activar": "Enable",
  "aj.p.falloCambiar": "Couldn't change it",
  "aj.p.ejemplo": "Counter, Bar...",
  "aj.p.anadir": "Add",
  "aj.p.fallo": "Couldn't create the till point",

  "su.titulo": "Your subscription",
  "su.prueba": "Trial period",
  "su.pruebaCaducada": "Trial ended",
  "su.alDia": "Up to date",
  "su.moroso": "Payment due",
  "su.suspendido": "Suspended",
  "su.sinCoste": "No charge",
  "su.alMes": "{importe} a month",
  "su.sinCuota": "This club has no subscription fee: you can use the program without paying anything.",
  "su.bloqueado":
    "The subscription has been unpaid for {dias} and the program is read-only. You can still look at everything and download the backups; as soon as the payment is logged, it all comes back.",
  "su.vencioAyer": "It expired yesterday. For now everything still works as normal.",
  "su.vencioHace": "It expired {dias} ago. For now everything still works as normal.",
  "su.pruebaTermina": "The trial ends on {fecha}, {cuando}.",
  "su.periodoHasta": "The paid period runs to {fecha}, {cuando}.",
  "su.hoy": "today",
  "su.manana": "tomorrow",
  "su.enDias": "in {dias}",
  "su.sinFecha": "There's no expiry date on record.",
  "su.pagos": "Payments logged",
  "su.sinPagos": "No payments logged yet.",
  "su.fecha": "Date",
  "su.importe": "Amount",
  "su.cubreHasta": "Covers until",
  "su.forma": "Method",
  "su.recibo": "Invoice",
  "su.metodo.TRANSFERENCIA": "Transfer",
  "su.metodo.EFECTIVO": "Cash",
  "su.metodo.DOMICILIACION": "Direct debit",
  "su.metodo.TARJETA": "Card",
  "su.metodo.OTRO": "Other",
  "su.r.imprimir": "Print or save as PDF",
  "su.r.suscripcion": "Management software subscription",
  "su.r.recibidoDe": "Received from",
  "su.r.fechaDelPago": "Payment date",
  "su.r.concepto": "Description",
  "su.r.conceptoTexto": "Management software subscription, up to {fecha}",
  "su.r.formaDePago": "Payment method",
  "su.r.importe": "Amount",
  "su.r.justificante":
    "Proof of the payment logged. It doesn't replace the tax invoice, which is issued by whoever provides the service.",

  "en.titulo": "Club management",
  "en.subtitulo": "Sign in with your team account",
  "en.email": "Email",
  "en.contrasena": "Password",
  "en.club": "Club",
  "en.eligeClub": "Choose the club...",
  "en.variosClubes": "That email belongs to several clubs: pick one.",
  "en.entrando": "Signing in...",
  "en.cola.una":
    "One collection on this tablet hasn't been sent: it was made offline and will go up as soon as you sign in.",
  "en.cola.varias":
    "There are {cuantas} on this tablet that haven't been sent: they were made offline and will go up as soon as you sign in.",
  "en.entrar": "Sign in",
  "en.fallo": "Couldn't sign in",
  "en.olvidada": "I've forgotten my password",
  "en.aunNoEsta": "Your club not here yet?",
  "en.darloDeAlta": "Set it up",
  "en.queEs": "What asocis is",

  "rc.titulo": "I've forgotten my password",
  "rc.subtitulo": "We'll send a link to your account's email",
  "rc.email": "Email",
  "rc.enviando": "Sending...",
  "rc.enviar": "Send me the link",
  "rc.fallo": "Couldn't send the email",
  "rc.miraSpam": "Check your junk folder too. The link expires in an hour.",
  "rc.volver": "Back to sign in",
  "rc.nueva": "New password",
  "rc.cambiada": "Password changed. Taking you to sign in...",
  "rc.entrarAhora": "Sign in now",
  "rc.nuevaEtiqueta": "New password",
  "rc.minimo": "At least 8 characters.",
  "rc.repitela": "Repeat it",
  "rc.noCoinciden": "The two passwords don't match.",
  "rc.cambiar": "Change the password",
  "rc.falloCambiar": "Couldn't change the password",

  "rg.cerrado": "Club sign-up closed",
  "rg.cerradoTexto":
    "This installation doesn't allow new clubs to be created from the web. Talk to whoever administers the server to get access.",
  "rg.volverAEntrar": "Back to sign in",
  "rg.titulo": "Set up a club",
  "rg.subtitulo": "You create the association and your owner account",
  "rg.nombreClub": "Association name",
  "rg.nombreClubEjemplo": "Cannabis Association...",
  "rg.ciudad": "Town",
  "rg.tuNombre": "Your name",
  "rg.tuEmail": "Your email",
  "rg.tuEmailAyuda": "You'll sign in with this.",
  "rg.contrasena": "Password",
  "rg.minimo": "At least 8 characters.",
  "rg.repite": "Repeat the password",
  "rg.codigo": "Licence code",
  "rg.codigoAyuda": "The one you were given when you signed up. It looks like this: ASOC-XXXX-XXXX.",
  "rg.acepto": "I've read and accept the",
  "rg.avisoLegal": "legal notice",
  "rg.yLa": "and the",
  "rg.privacidad": "privacy policy",
  "rg.creando": "Creating the club...",
  "rg.crear": "Create the club",
  "rg.yaTeneis": "Already have a club?",
  "rg.entrar": "Sign in",
  "rg.noCoinciden": "The two passwords don't match",
  "rg.fallo": "Couldn't create the club",
};
